import { DataType, DateTime, ExprCall, Ident } from '@mode-switch/express';
import { Attr, Names, Values } from '@mode-switch/model';
import {
  BasicChannel,
  BatchEncoder,
  ColorTarget,
  EMPTY_ENCODING,
  EncChannel,
  EncContinuousDateFilterField,
  EncContinuousFilterField,
  EncDiscreteFilterField,
  EncDualAxisXField,
  EncDualAxisYField,
  EncField,
  EncFilterField,
  EncMarks,
  EncRelativeDateFilterField,
  EncSeries,
  EncSort,
  Encoding,
  EncodingResult,
  ExprFrame,
  FilterType,
  Guide,
  MarkChannel,
  MarkType,
  MarkTypeOptions,
  MarksSummary,
  ModifyField,
  DataType as ReportDataType,
  SelectionMode,
  SortType,
  TextDisplay,
  VariableType,
} from '@mode-vegas/vegas';
import { ChartTypes, FlamingoTypes, ReportTypes } from '@mode/shared/contract-common';
import { DEFAULT_CHART_FORMAT, escapeAttrName, generateUUID, isPresent } from '@mode/shared/util-js';
import { CompiledField, CompiledFormula, FormulaOperations, Switch } from '@mode/shared/util-switch';
import { isValid } from 'date-fns';
import { Record as ImmutableRecord } from 'immutable';
import { findLast, uniqBy } from 'lodash';
import { ChartFormatHelpers, ChartTranslator, ChartTranslatorUtil, SharedAxisHelpers, VegasFrame } from '..';
import { ChartHierarchy } from './chart-hierarchy';
import { SchemaModel } from './schema-model';

const StandardXYChartTypes: Set<ChartTypes.ChartType> = new Set([
  'line',
  'stackedBar',
  'hStackedBar',
  'stackedBar100',
  'hStackedBar100',
  'area',
  'totalArea',
  'scatter',
] as ChartTypes.ChartType[]);
const GroupedBarChartTypes: Set<ChartTypes.ChartType> = new Set(['bar', 'hBar'] as ChartTypes.ChartType[]);
const PieChartTypes: Set<ChartTypes.ChartType> = new Set(['donut', 'pie'] as ChartTypes.ChartType[]);
export const HorizontalChartTypes = new Set(['hStackedBar', 'hBar', 'hStackedBar100'] as ChartTypes.ChartType[]);

const guide = new Guide();

export enum ZoneType {
  Discrete = 'DISCRETE',
  Continuous = 'CONTINUOUS',
  Aggregate = 'AGGREGATE',
}

const chartToMarkType: Record<string, MarkType> = {
  line: MarkType.Line,
  bar: MarkType.Bar,
  hBar: MarkType.Bar,
  stackedBar: MarkType.Bar,
  stackedBar100: MarkType.Bar,
  hStackedBar: MarkType.Bar,
  hStackedBar100: MarkType.Bar,
  area: MarkType.Area,
  totalArea: MarkType.TotalArea,
  pie: MarkType.Pie,
  donut: MarkType.Donut,
  scatter: MarkType.Circle,
};

const NAMES_FIELD: EncField = {
  formula: Names.Key,
  variable: { type: VariableType.DISCRETE },
};

export const VALUES_FIELD: EncField = {
  formula: Values.Key,
  variable: { type: VariableType.CONTINUOUS },
};

export const DateTimePatterns: Record<string, RegExp> = {
  year: /^\d{4}$/,
  quarter: /^Q\d\s\d{4}$/,
  month: /^[A-Z][a-z]{2}\s\d{4}$/,
  week: /^[A-Z][a-z]{2}\s\d\d?\s\d{4}$/,
  day: /^[A-Z][a-z]{2}\s\d\d?\s\d{4}$/,
  hour: /^\d\d?am|pm\s[A-Z][a-z]{2}\s\d\d?\s\d{4}$/,
  minute: /^\d\d?:\d{2}am|pm\s[A-Z][a-z]{2}\s\d\d?\s\d{4}$/,
  second: /^\d\d?:\d{2}:\d{2}am|pm\s[A-Z][a-z]{2}\s\d\d?\s\d{4}$/,
};

/**
 * Interface for BuildDefaultEncField Function.
 *
 * @interface BuildDefaultEncField
 * @property {Object} buildDefaultEncField.field     - CompiledFormula
 * @property {string} [buildDefaultEncField.zone]    - ZoneType
 * @property {string} [buildDefaultEncField.channel] - EncChannel
 * @property {Object} [buildDefaultEncField.frame]   - ExprFrame
 */
export interface BuildDefaultEncField {
  field: CompiledFormula;
  zone?: ZoneType;
  channel?: EncChannel;
  frame?: ExprFrame;
}

export function setFrame(encoding: Encoding, id: string, frame: ExprFrame | undefined) {
  const batch = guide.newBatchOperation(encoding);
  batch.setFrame(id, frame);
  return batch.complete();
}

export function clearFrame(enc: Encoding, id: string | undefined): Encoding {
  return typeof id === 'string' ? clearFrames(enc, [id]) : enc;
}

export function clearFrames(enc: Encoding, ids: string[] | undefined): Encoding {
  if (!Array.isArray(ids) || ids.length === 0) {
    return enc;
  }

  const batch = guide.newBatchOperation(enc);
  ids.forEach((id) => {
    batch.clearFrame(id);
  });

  const result = batch.complete();
  return result.encoding;
}

export interface EncSortFields {
  field: EncField;
  by?: EncField;
}

export function findSortByFields(encoding: Encoding, chartType?: ChartTypes.ChartType): EncSortFields | void {
  switch (chartType) {
    case 'bigValue': {
      const field = encoding.y[0];

      // Preserved from previous implementation..
      // No byField sort for BigValue
      return typeof field === 'undefined'
        ? undefined
        : {
            field,
          };
    }
    default: {
      return findSortByFieldsVegas(encoding);
    }
  }
}

function findSortByFieldsVegas(encoding: Encoding): EncSortFields | void {
  const { marks } = encoding;

  /**
   * @description Array of 1+ channel's fields to search for a field _to sort_.
   * We search from RIGHT -> LEFT when looking for the sort field which is intended
   * to produce the _inner most_ discrete field
   */
  let sortFields: readonly EncField[] = [];

  /**
   * @description Array of 1+ channel's fields to search for a field to _sort by_.
   * We search from LEFT -> RIGHT when looking for the by-field which is intended
   * to produce the _outer most_ continuous field
   */
  let sortByFields: readonly EncField[] = [];

  switch (marks.channel) {
    case 'x': {
      sortFields = [...encoding.column, ...encoding.x, ...encoding.row, ...encoding.y];
      sortByFields = encoding.x;
      break;
    }
    case 'y': {
      sortFields = [...encoding.row, ...encoding.y, ...encoding.column, ...encoding.x];
      sortByFields = encoding.y;
      break;
    }
    case '*': {
      switch (marks.properties.type.value) {
        // donut|pie is color by angle
        case MarkType.Donut:
        case MarkType.Pie: {
          sortFields = marks.properties.color.values;
          sortByFields = marks.properties.angle.values;
          break;
        }
        default: {
          sortFields = [...encoding.column, ...encoding.x, ...encoding.row, ...encoding.y];
          sortByFields = [
            ...marks.properties.text.values,
            ...marks.properties.color.values,
            ...marks.properties.size.values,
            ...(marks.properties.shape ?? []),
          ];
          break;
        }
      }
      break;
    }
  }

  /**
   * @description Field to sort is the _inner most_ discrete field
   */
  let field = findLast(sortFields, (field) => {
    return field.variable.type === VariableType.DISCRETE;
  });

  // Bail if we don't have a field to sort
  if (field == null) {
    return;
  }

  /**
   * @description Field to sort _by_ is the _outer most_ continuous field.
   * Undefined is an acceptable value here, we'll just sort the field by
   * DATA ASC|DESC
   */
  let by = sortByFields.find((field) => {
    return field.variable.type === VariableType.CONTINUOUS;
  });

  /**
   * Sorting Measure Names (MN) by Measure Values (MV)
   * ---
   *
   * ## Field to order is color when
   *
   * 1. Sort MN by MV
   * 2. One field in values
   * 3. Color channel value is not MN
   *
   * In Quick Charts, this is a case of Grouped Bar. When Measure Names
   * is on the secondary axis, Measure Values includes one measure, and
   * a field has been "manually" added to the color channel
   */
  if (field.formula === Names.Key && by?.formula === Values.Key) {
    if (encoding.value.length === 1) {
      const series = marks.series.find((series) => series.field === by?.id);
      const props = series?.properties ?? marks.properties;
      const color = props.color.values[0];

      // Can promote color field as the sort field if:
      // 1. It is discrete
      // 2. It is not Measure Names
      //
      if (color?.variable.type === VariableType.DISCRETE && color.formula !== Names.Key) {
        field = color;
      }
    }
  }

  return {
    field,
    by,
  };
}

export function quickSort(enc: Encoding, descending: boolean, chartType?: ChartTypes.ChartType): EncodingResult {
  const sortFields = findSortByFields(enc, chartType);
  if (sortFields) {
    if (sortFields.by) {
      return guide.addSort(enc, createNestedOrByFieldSort(enc, sortFields.field, sortFields.by, descending));
    } else {
      return guide.addSort(enc, {
        type: SortType.DATA,
        source: sortFields.field.formula,
        descending,
      });
    }
  }
  return guide.load(enc);
}

export function createByFieldSort(field: EncField, by: EncField, desc: boolean) {
  return EncSort.ByField.make(field.formula, by, desc);
}

export function createNestedOrByFieldSort(enc: Encoding, field: EncField, by: EncField, desc: boolean) {
  // Nested sort will be applied when:
  // ---
  // 1) The field to order is not Measure Names
  // 2) The field to order _by_ is not Measure Values
  // 3) The field to order has 1+ outer discrete fields
  if (field.formula !== Names.Key && by.formula !== Values.Key) {
    const outer = getBasicOuterFields(enc, field);

    // Is inner field of {column,x} | {row,y}? apply nested sort!
    //
    // Enc {
    //  row [ region ] <- "outer"
    //  y [ rep ]      <- "inner"
    //  sort [
    //    Nested(y:rep, sum(price), desc)
    //  ]
    // }
    if (outer.length !== 0) {
      const id = Ident.next('ENC');
      return EncSort.ByNested.make(
        field.id as string,
        field.formula,
        {
          ...by,
          id,
        },
        desc
      );
    }
  }

  return createByFieldSort(field, by, desc);
}

export function getBasicOuterFields(enc: Encoding, field: EncField | string) {
  const alias = typeof field === 'string' ? field : (field.id as string);
  const column = enc.column.concat(enc.x);
  const row = enc.row.concat(enc.y);

  const outer: EncField[] = [];
  for (const channel of [column, row]) {
    const index = channel.findIndex((f) => f.id === alias);
    if (index !== -1) {
      for (const field of channel.slice(0, index)) {
        if (field.variable.type === VariableType.DISCRETE && field.formula !== Names.Key) {
          outer.push(field);
        }
      }
      break;
    }
  }

  return outer;
}

export function getField(enc: Encoding, formula: string): EncField | undefined {
  return enc.marks.properties.text.values.find((field) => field.formula === formula);
}

/** Add sort to table encoding */
export function addTableSort(
  encoding: Encoding,
  formula: string,
  descending: boolean,
  clearExistingSorts = true
): EncodingResult {
  const encoder = guide.newBatchOperation(encoding);

  if (clearExistingSorts) {
    encoder.clearSorts();
  }
  encoder.addSort({
    type: SortType.DATA,
    source: formula,
    descending,
  });

  return encoder.complete();
}

export function addSort(enc: Encoding, sort: EncSort): EncodingResult {
  switch (sort.type) {
    case SortType.ALPHA:
    case SortType.DATA:
    case SortType.FIELD:
    case SortType.NESTED:
    case SortType.MANUAL: {
      return guide.addSort(enc, sort);
    }
    default: {
      return guide.load(enc);
    }
  }
}

export function addMeasureValuesToColorChannel(encoding: Encoding) {
  const encoder = guide.newBatchOperation(encoding);

  encoder.addEncFieldToSeries({ ...VALUES_FIELD }, 'color', '*');
  return encoder.complete();
}

export function addMeasureNamesToColorChannel(encoding: Encoding, series: string | undefined) {
  const encoder = guide.newBatchOperation(encoding);

  encoder.addEncFieldToSeries({ ...NAMES_FIELD }, 'color', series);
  return encoder.complete();
}

export function removeSort(encoding: Encoding, source: string) {
  return guide.removeSort(encoding, source);
}

export function clearFilters(encoding: Encoding) {
  const encoder = guide.newBatchOperation(encoding);
  for (const field of encoding.filter) {
    encoder.removeFilter(field.formula, field.variable.type === VariableType.DISCRETE);
  }

  return encoder.complete();
}

export function clearSorts(encoding: Encoding) {
  const encoder = guide.newBatchOperation(encoding);
  for (const field of encoding.sorts) {
    encoder.removeSort(field.source);
  }

  return encoder.complete();
}

export class EncResult extends ImmutableRecord<EncodingResult>({
  encoding: EMPTY_ENCODING,
  summary: new MarksSummary(),
}) {}

export function getSummary(encoding: Encoding): MarksSummary {
  return guide.load(encoding).summary;
}

export function initEncoding(chartType: ChartTypes.ChartType): EncodingResult {
  const encoder = guide.newBatchOperation(EMPTY_ENCODING);

  switch (chartType) {
    default:
    case 'line':
      encoder.addEncField({ ...VALUES_FIELD }, 'row');
      encoder.addEncFieldToSeries({ ...NAMES_FIELD }, 'color');
      encoder.updateMarkType(MarkType.Line);
      break;
    case 'hBar':
      encoder.addEncField({ ...VALUES_FIELD }, 'column');
      encoder.addEncField({ ...NAMES_FIELD }, 'row');
      encoder.addEncFieldToSeries({ ...NAMES_FIELD }, 'color');
      encoder.updateMarkType(MarkType.Bar);
      break;
    case 'bar':
      encoder.addEncField({ ...VALUES_FIELD }, 'row');
      encoder.addEncField({ ...NAMES_FIELD }, 'column');
      encoder.addEncFieldToSeries({ ...NAMES_FIELD }, 'color');
      encoder.updateMarkType(MarkType.Bar);
      break;
    case 'hStackedBar': // falls through
    case 'hStackedBar100':
      encoder.addEncField({ ...VALUES_FIELD }, 'column');
      encoder.addEncFieldToSeries({ ...NAMES_FIELD }, 'color');
      encoder.updateMarkType(MarkType.Bar);
      break;
    case 'vegas':
      encoder.updateMarkType(MarkType.Bar);
      break;
    case 'stackedBar': // falls through
    case 'stackedBar100':
      encoder.addEncField({ ...VALUES_FIELD }, 'row');
      encoder.addEncFieldToSeries({ ...NAMES_FIELD }, 'color');
      encoder.updateMarkType(MarkType.Bar);
      break;
    case 'area':
      encoder.addEncField({ ...VALUES_FIELD }, 'row');
      encoder.addEncFieldToSeries({ ...NAMES_FIELD }, 'color');
      encoder.updateMarkType(MarkType.Area);
      break;
    case 'totalArea':
      encoder.addEncField({ ...VALUES_FIELD }, 'row');
      encoder.addEncFieldToSeries({ ...NAMES_FIELD }, 'color');
      encoder.updateMarkType(MarkType.TotalArea);
      break;
    case 'pie':
      encoder.updateMarkType(MarkType.Pie, false, '*');
      break;
    case 'donut':
      encoder.updateMarkType(MarkType.Donut, false, '*');
      break;
    case 'scatter':
      encoder.addEncField({ ...VALUES_FIELD }, 'row');
      encoder.addEncFieldToSeries({ ...NAMES_FIELD }, 'color');
      encoder.updateMarkType(MarkType.Circle);
      break;
    case 'vegasPivotTable':
      encoder.addEncField({ ...NAMES_FIELD }, 'row');
      encoder.addEncFieldToSeries({ ...VALUES_FIELD }, 'text', '*');
      encoder.updateMarkType(MarkType.Text, false, '*');
      encoder.addEncFieldToSeries({ ...VALUES_FIELD }, 'color', '*');
      encoder.updateMarkColor({ target: 'background' });
      break;
    case 'linePlusBar': // falls through
    case 'table': // falls through
    case 'bigValue':
      break;
    case 'histogram':
      encoder.updateMarkType(MarkType.Bar);
      break;
  }

  encoder.autoEncode();
  return encoder.complete();
}

export function loadEncoding(encoding: Encoding): EncodingResult {
  return guide.load(encoding);
}

export function normalizeEncoding(encoding: Encoding) {
  return encoding != null ? guide.load(encoding).encoding : encoding;
}

export function batchEncoding(encoding: Encoding): BatchEncoder {
  return guide.newBatchOperation(encoding);
}

/**
 * Changes the automatic mark type if needed.
 *
 * @param {ModeView} view mode view
 * @param {Encoding} oldEncoding previous encoding
 * @param {EncodingResult} newEncodingResult new encoding result
 * @param {generatedAutoMarkType} generatedAutoMarkType generated auto mark type that is passed in when generateAutomaticMarkType function is called separately
 * @return {EncodingResult} new encoding result, modified with new mark type if needed
 */
export function updateAutomaticMarkType(
  view: FlamingoTypes.ModeView,
  oldEncoding: Encoding,
  newEncodingResult: EncodingResult,
  generatedAutoMarkType: MarkType | null = null
): EncodingResult {
  const newEncoding = newEncodingResult.encoding;

  // Check for null or automatic marks.
  let isAutomatic = false;
  if (newEncoding.marks.series.length) {
    for (const series of newEncoding.marks.series) {
      if (series.properties.type.value == null || series.properties.type.automatic) {
        isAutomatic = true;
      }
    }
  } else if (newEncoding.marks.properties.type.value == null || newEncoding.marks.properties.type.automatic) {
    isAutomatic = true;
  }

  // Update any null or automatic marks.
  if (isAutomatic) {
    const markType = generatedAutoMarkType || generateAutomaticMarkType(view, newEncoding);
    const encoder = guide.newBatchOperation(newEncoding);
    if (newEncoding.marks.series.length) {
      for (const series of newEncoding.marks.series) {
        if (series.properties.type.value == null || series.properties.type.automatic) {
          encoder.updateMarkType(markType, true, series.field);
        }
      }
    } else if (newEncoding.marks.properties.type.value == null || newEncoding.marks.properties.type.automatic) {
      encoder.updateMarkType(markType, true, '*');
    }
    newEncodingResult = encoder.complete();
  }

  // Return either the specified EncodingResult, or a new one with the new mark type.
  return newEncodingResult;
}

/**
 * Returns automatic mark type.
 *
 * If either axis has continuous fields without aggregates, or both axes have only aggregates, use shape;
 * or if on one axis has aggregates, and the other has continuous or temporal fields, use line;
 * or if any axes have continuous fields, use bar;
 * otherwise, use text.
 *
 * TODO: Consider the size of the data set and use heatmaps when they become available.
 */
export function generateAutomaticMarkType(view: FlamingoTypes.ModeView, encoding: Encoding) {
  const getAutomaticMarkType = (
    nXCont: number,
    nYCont: number,
    nXAggr: number,
    nYAggr: number,
    nXDate: number,
    nYDate: number
  ) => {
    let markType: MarkType;
    if (nXCont > nXAggr && nYCont === 0) {
      markType = MarkType.Circle;
    } else if (nYCont > nYAggr && nXCont === 0) {
      markType = MarkType.Circle;
    } else if (nXCont > nXAggr && nYCont > nYAggr) {
      markType = MarkType.Circle;
    } else if (nXCont === nXAggr && nYCont === nYAggr && nXCont > 0 && nYCont > 0) {
      markType = MarkType.Circle;
    } else if (nXAggr > 0 && nYCont + nYDate > nYAggr) {
      markType = MarkType.Line;
    } else if (nYAggr > 0 && nXCont + nXDate > nXAggr) {
      markType = MarkType.Line;
    } else if (nXCont + nYCont > 0) {
      markType = MarkType.Bar;
    } else {
      markType = MarkType.Text;
    }
    return markType;
  };

  // Get the old and new axis fields.
  const x = [...encoding.x, ...encoding.x2];
  const y = [...encoding.y, ...encoding.y2];

  // Returns true iff compiled field meets specified condition.
  const isFieldSuchThat = (encField: EncField, condition: (compiledField: CompiledField) => boolean) => {
    let isTrue = false;
    if (encField.id) {
      const compiledField = CompiledField.fromEncField(view, encField);
      if (compiledField.isValid) {
        isTrue = condition(compiledField);
      }
    }
    return isTrue;
  };

  // Count the continuous axis fields.
  const isContinuous = (encField: EncField) => encField.variable.type === VariableType.CONTINUOUS;
  const nXCont = x.filter(isContinuous).length;
  const nYCont = y.filter(isContinuous).length;

  // Identify const and scalar fields.
  const isConstOrScalar = (compiledField: CompiledField) => compiledField.isAggregateable;

  // Count the aggregate/analytic/binned fields.
  const isAggregateOrAnalytic = (encField: EncField) =>
    isFieldSuchThat(encField, (compiledField: CompiledField) => !isConstOrScalar(compiledField));
  const nXAggr = x.filter(isAggregateOrAnalytic).length;
  const nYAggr = y.filter(isAggregateOrAnalytic).length;

  // Count the temporal fields.
  const isTemporal = (encField: EncField) =>
    isFieldSuchThat(
      encField,
      (compiledField: CompiledField) =>
        compiledField.hasDateFunc || compiledField.isAttrDateType || compiledField.isFormulaDate
    );
  const nXDate = x.filter(isTemporal).length;
  const nYDate = y.filter(isTemporal).length;

  // Return the automatic mark type.
  return getAutomaticMarkType(nXCont, nYCont, nXAggr, nYAggr, nXDate, nYDate);
}

// Updates field encoding.
export function updateFieldEncoding(encoding: Encoding, modify: ModifyField): EncodingResult {
  const encoder = guide.newBatchOperation(encoding);
  encoder.updateField(modify);
  return encoder.complete();
}

// Updates field encoding and updates automatic mark type if needed.
export function updateVegasFieldEncoding(
  view: FlamingoTypes.ModeView,
  encoding: Encoding,
  modify: ModifyField
): EncodingResult {
  return updateAutomaticMarkType(view, encoding, updateFieldEncoding(encoding, modify));
}

type ModifyAllField = Omit<ModifyField, 'id'>;
export function updateAllFieldEncoding(encoding: Encoding, fieldIds: string[], modify: ModifyAllField): EncodingResult {
  const encoder = guide.newBatchOperation(encoding);
  fieldIds.forEach((fieldId: string, index: number) => {
    encoder.updateField({
      ...modify,
      id: fieldId,
      series: encoding.marks.series[index]?.field,
    });
  });
  return encoder.complete();
}

export function extractPrimarySecondaryAxes(enc: Encoding) {
  const primary = enc.marks.channel === 'x' ? enc.x : enc.y;
  const secondary = enc.marks.channel === 'x' ? enc.y : enc.x;
  const secondaryDiscrete = enc.marks.channel === 'x' ? enc.row : enc.column;

  return {
    primary,
    secondary,
    secondaryDiscrete,
  };
}

export function ensureVerticalEncoding(encoding: Encoding) {
  if (encoding.marks.channel === 'x') {
    return swapAxes(encoding);
  }

  return encoding;
}

export function swapAxes(encoding: Encoding) {
  return guide.swapRowsColumns(encoding).encoding;
}

/**
 * @description Just like swapAxes but for one field only
 * Used for PTQC flow - swapping axes for measure names
 * @param encoding Current chart encoding
 * @param id id of field to move
 */
export function swapRowColAxes(encoding: Encoding, id: string) {
  const inRow = encoding.y.find((field) => field.id === id) || encoding.row.find((field) => field.id === id);
  const encResult = loadEncoding(encoding);
  return updateOrMoveVegasField(encResult, id, inRow ? 'column' : 'row');
}

export function toggleDualAxisField(enc: Encoding, rightFieldId: string): EncodingResult {
  return guide.toggleDualAxisField(enc, rightFieldId);
}

export function updateDualAxisField(enc: Encoding, rightFieldId: string): EncodingResult {
  const encoder = guide.newBatchOperation(enc);
  encoder.toggleDualAxisField(rightFieldId);
  return encoder.complete();
}

export function removeFilterFromEncoding(encoding: Encoding, filterField: EncFilterField) {
  const encoder = guide.newBatchOperation(encoding);
  encoder.removeFilter(filterField.formula, filterField.variable.type === VariableType.DISCRETE);
  return encoder.complete();
}

/**
 * @description Upserts the filter into the encoding on formula and variable type. If the filter has
 * an id matching that of another filter in the encoding, the new filter will replace the existing
 * filter prior to the upsert on formula and variable type
 */
export function addFilterToChartEncoding(encoding: Encoding, filter: EncFilterField, field: CompiledField) {
  const removeIds: string[] = filter.id ? [filter.id] : [];

  /**
   * DATEPART and DATETRUNC formulas can have the same output while having signatures
   * that differ on weekStart and yearStart.. weekStart only applies to the unit WEEK,
   * and yearStart only applies to units YEAR|QUARTER; eg, the following formula pairs
   * yield the same data:
   *
   * ** formula -> display name **
   *
   * DATETRUNC("DAY", [date], "MONDAY", "APRIL") -> DAY(date)
   * DATETRUNC("DAY", [date], "FRIDAY", "MARCH") -> DAY(date)
   *
   * DATEPART("YEAR", [date], "MONDAY", "APRIL") -> YEAR(date, APRIL)
   * DATEPART("YEAR", [date], "FRIDAY", "APRIL") -> YEAR(date, APRIL)
   *
   * DATEPART("WEEK", [date], "MONDAY", "APRIL") -> WEEK(date, MONDAY)
   * DATEPART("WEEK", [date], "MONDAY", "MARCH") -> WEEK(date, MONDAY)
   *
   * ---
   *
   * Currently, the Encoder only knows how to match duplicate filters on
   * filter formula and variable type.. so we need to help out a bit. If
   * the [filter] field we're adding is DATETRUNC|DATEPART, look for any
   * other filters that match it by applied function, variable type, and
   * display name.. then remove them. In practice there *should* only be
   * one matching field to remove, if any. If a matching field was found
   * we'll replace it with the new filter field
   *
   */

  const varType = Switch.Compat.fromVegasVarType(filter.variable.type);
  const filters = encoding.filter.filter((encFilter) => {
    return encFilter.variable.type === filter.variable.type;
  });

  for (const encFilter of filters) {
    const filterField = field.withNewFormula(encFilter.formula, varType);
    if (filterField.form === field.form && filterField.displayName === field.displayName) {
      if (typeof encFilter.id === 'string') {
        removeIds.unshift(encFilter.id);
      }
    }
  }

  const uuid = generateUUID();
  const encoder = guide.newBatchOperation(encoding);
  const hotswap = { ...filter, id: uuid, formula: uuid };

  if (removeIds.length > 0) {
    encoder.addEncFilter(hotswap, removeIds[0]);
    encoder.removeFields(removeIds);
  }

  encoder.addEncFilter(filter, uuid);
  encoder.removeField(uuid);

  return encoder.complete();
}

// TODO: this functionality should eventually be moved into Vegas, either into ModifyField
//       or into some new ModifyFilterField thing
export function modifyFilterFieldContinuousToDiscrete(
  enc: EncContinuousFilterField | EncContinuousDateFilterField | EncRelativeDateFilterField
): EncDiscreteFilterField {
  return {
    filterType: FilterType.Discrete,
    formula: enc.formula,
    variable: { type: VariableType.DISCRETE },
    selection: {
      selected: [],
      mode: SelectionMode.ALL,
    },
  };
}

export function modifyFilterFieldDiscreteToContinuous(
  view: FlamingoTypes.ModeView,
  enc: EncDiscreteFilterField,
  useRelativeDate = false
): EncContinuousFilterField | EncContinuousDateFilterField | EncRelativeDateFilterField | null {
  let range: { min: number | null; max: number | null };
  switch (enc.selection.mode) {
    case SelectionMode.ALL:
    case SelectionMode.EXCLUDE:
    case SelectionMode.EXCLUDE_ALL: {
      range = { min: null, max: null };
      break;
    }
    case SelectionMode.INCLUDE: {
      range = {
        min: Math.min(...(enc.selection.selected as number[])),
        max: Math.max(...(enc.selection.selected as number[])),
      };
      break;
    }
    default: {
      throw new Error(`Unexpected SelectionMode of filter being modified: ${enc.selection.mode}`);
    }
  }

  const compiled = CompiledField.fromEncField(view, enc);
  if (compiled.isValid) {
    if (compiled.isFormulaDate) {
      if (useRelativeDate) {
        // If both from/to are fixed types with NaN then the filter modal will
        // attempt to fill in the min/max values with the field's domain values
        const min = Number.isFinite(range.min) ? (range.min as number) : NaN;
        const max = Number.isFinite(range.max) ? (range.max as number) : NaN;
        return {
          filterType: FilterType.RelativeDate,
          formula: enc.formula,
          variable: { type: VariableType.CONTINUOUS },
          includeNulls: false,
          from: {
            type: 'fixed',
            value: min,
          },
          to: {
            type: 'fixed',
            value: max,
          },
        };
      }

      return {
        filterType: FilterType.ContinuousDate,
        formula: enc.formula,
        variable: { type: VariableType.CONTINUOUS },
        includeNulls: false,
        includeMin: true,
        includeMax: true,
        range: {
          min: range.min as number,
          max: range.max as number,
        },
      };
    }

    return {
      filterType: FilterType.Continuous,
      formula: enc.formula,
      variable: { type: VariableType.CONTINUOUS },
      range,
      includeMin: true,
      includeMax: true,
      includeNulls: false,
    } as EncContinuousFilterField;
  }

  return null;
}

export function updateFieldMarkType(
  enc: Encoding,
  series: string | undefined,
  markType: MarkType | null
): EncodingResult {
  const encoder = guide.newBatchOperation(enc);
  encoder.updateMarkType(markType, markType == null, series);
  return encoder.complete();
}

export function getEncFieldById(enc: Encoding, id: string): EncField {
  const encoder = guide.newBatchOperation(enc);
  const field = encoder.getFieldById(id);
  return field;
}

export function getModeFieldById(model: SchemaModel, enc: Encoding, id: string): FlamingoTypes.ModeField | undefined {
  const encField = getEncFieldById(enc, id);
  return model.makeField(encField);
}

export function getSeriesFieldById(encoding: Encoding, id: string): EncField | undefined {
  const x = encoding.x && encoding.x.find((x) => x.id === id);
  const x2 = encoding.x2 && encoding.x2.find((x2) => x2.id === id);
  const y = encoding.y && encoding.y.find((y) => y.id === id);
  const y2 = encoding.y2 && encoding.y2.find((y2) => y2.id === id);
  const row = encoding.row && encoding.row.find((r) => r.id === id);
  const column = encoding.column && encoding.column.find((c) => c.id === id);

  return x || x2 || y || y2 || row || column;
}

function validateEncodingField(field: EncField, encoding: Encoding, model: SchemaModel, errors: string[]): void {
  const [isValid, err] = model.validateFormula(field.formula);
  if (!isValid && err != null) {
    errors.push(err);
  }
  // Validate frame
  if (isValid && field.frame) {
    const { error } = validateEncodingFieldFrame(field, encoding, model);
    if (error) {
      errors.push(error);
    }
  }
}

function validateEncodingValueFieldStage(field: EncField, model: SchemaModel, errors: string[]): void {
  const compiled = model.compileEncField(field);
  if (compiled.isScalar) {
    errors.push(`Fields in Values must be Const, Aggregate, or Analytic`);
  }
}

export function validateEncSortField(sort: EncSort, model: SchemaModel, errors: string[] = []): string[] {
  const byFieldFormula =
    sort.type === SortType.FIELD || sort.type === SortType.NESTED ? sort.byField.formula : undefined;
  if (!validateFormulaAndByField(sort.source, byFieldFormula, model)) {
    errors.push(`Sort on ${sort.source} is invalid`);
  }
  return errors;
}

export function validateFilterField(filter: EncFilterField, model: SchemaModel, errors: string[] = []): string[] {
  // Basic validation of the filter field
  const [isValid, msg] = model.validateFilter(filter, false);
  if (!isValid && msg != null) {
    errors.push(msg);
    return errors;
  }
  // Validate the field and by-field formula
  const byFieldFormula = filter.filterType === FilterType.TopByField ? filter.cond.byField.formula : undefined;
  if (!validateFormulaAndByField(filter.formula, byFieldFormula, model)) {
    errors.push(`Filter on ${filter.formula} is invalid`);
  }
  // Validate the by-field isn't an analytic calculation
  else if (filter.filterType === FilterType.TopByField) {
    const compiledByField = model.compileEncField(filter.cond.byField);
    if (!compiledByField.isValid || compiledByField.isAnalyticCalc) {
      errors.push(`Filter condition on ${compiledByField.attrName} is invalid`);
    }
  }
  return errors;
}

function validateFormulaAndByField(formula: string, byFieldFormula: string | undefined, model: SchemaModel) {
  if (model == null) {
    return true;
  }
  const [isValid] = model.validateFormula(formula);
  const [byFieldValid] = byFieldFormula ? model.validateFormula(byFieldFormula) : [true, null];
  return isValid && byFieldValid;
}

export function validateTableEncodingFields(encoding: Encoding, model: SchemaModel | undefined): string[] {
  const errors: string[] = [];

  if (!(encoding && model)) {
    return errors;
  }

  for (const filter of encoding.filter) {
    const [isValid, msg] = model.validateFilter(filter, false);
    if (!isValid && msg != null) {
      errors.push(msg);
    }
  }

  encoding.marks.properties.text.values.forEach((field) => {
    validateEncodingField(field, encoding, model, errors);
    const compiled = model.compileEncField(field);
    // Only block analytic calcs (e.g., INDEX, LOOKUP) - aggregate calcs are now supported
    if (compiled.isAnalyticCalc) {
      errors.push(`Analytic values are not supported in table columns: ${compiled.displayName}`);
    }
  });

  return errors;
}

export function validateChartEncodingFields(encoding: Encoding, model: SchemaModel | undefined): string[] {
  const errors: string[] = [];

  if (!(encoding && model)) {
    return errors;
  }

  const fields = collectEncFields(encoding);
  for (const field of fields) {
    validateEncodingField(field, encoding, model, errors);
  }

  // If MV field is present, validate the stage of the value fields
  if (fields.some((f) => f.formula === Values.Key)) {
    for (const field of encoding.value) {
      validateEncodingValueFieldStage(field, model, errors);
    }
  }

  for (const sort of encoding.sorts) {
    validateEncSortField(sort, model, errors);
  }

  for (const filter of encoding.filter) {
    const [isValid, msg] = model.validateFilter(filter, true);
    if (!isValid && msg != null) {
      errors.push(msg);
    }
  }

  return Array.from(new Set(errors));
}

export function canSplitLegends(encoding: Encoding, seriesId: string | undefined) {
  // Measure Names has to be in the encoding
  if (!findInEncoding(encoding, Names.Key)) {
    return false;
  }
  if (encoding.marks.channel === '*') {
    return encoding.marks.properties.color.values[0]?.formula === Values.Key;
  }
  const series =
    seriesId !== undefined ? encoding.marks.series.find((s) => s.field === seriesId) : encoding.marks.series[0];

  return series != null && series.properties.color.values[0]?.formula === Values.Key;
}

export function canChangeHierarchy(
  encoding: Encoding,
  model: FlamingoTypes.ModeModel,
  viewName: string
): { back: boolean; forward: boolean } {
  if (encoding && model) {
    const view = Switch.Views.find(model, viewName);
    if (view == null) {
      return { back: false, forward: false };
    }

    const hierarchyField = getHierarchyField(encoding, view);
    if (hierarchyField?.dateFuncUnit !== undefined) {
      const hierarchy = hierarchyField.hasDateTrunc
        ? ChartHierarchy.DateTruncHierarchy
        : ChartHierarchy.DatePartHierarchy;
      const index = hierarchy.indexOf(hierarchyField.dateFuncUnit);
      return { back: index > 0, forward: index < hierarchy.length - 1 };
    }
  }

  return { back: false, forward: false };
}

export function changeHierarchy(encoding: Encoding, view: FlamingoTypes.ModeView, forward: boolean): EncodingResult {
  const hierarchyField = getHierarchyField(encoding, view);
  if (hierarchyField?.dateFuncUnit !== undefined) {
    // Save any sort that might exist on the hierarchy field
    const existingSort = encoding.sorts.find(
      (encSort) => encSort.type !== SortType.MANUAL && encSort.source === hierarchyField.formula
    );

    const truncOrPart = hierarchyField.hasDatePart ? FormulaOperations.datePart : FormulaOperations.dateTrunc;
    const nextField = hierarchyField.withNewFormula(
      truncOrPart(
        hierarchyField,
        ChartHierarchy.getNextDatePart(hierarchyField.dateFuncUnit, hierarchyField.hasDateTrunc, forward)
      )
    );
    let result = guide.updateField(encoding, nextField.toModifyField());
    if (existingSort) {
      // Remove the old sort (technically 'updateField' should have already removed it)
      result = guide.removeSort(result.encoding, existingSort.source);
      // Add the sort back with the new formula
      result = guide.addSort(result.encoding, { ...existingSort, source: nextField.formula });
    }

    return result;
  }

  // Do nothing
  return guide.load(encoding);
}

function getHierarchyField(encoding: Encoding, view: FlamingoTypes.ModeView) {
  // Return the first date field from the positional channels in order
  // specified by the marks channel. We favor secondary fields over primary
  // fields. We also choose the field closest to the axis so we start at x|y
  // then work backwards through the column|row from inner to outer.
  const candidateFields =
    encoding.marks.channel === 'x'
      ? [...encoding.y, ...[...encoding.row].reverse(), ...encoding.x, ...[...encoding.column].reverse()]
      : [...encoding.x, ...[...encoding.column].reverse(), ...encoding.y, ...[...encoding.row].reverse()];
  const hierarchyField = candidateFields.find((encField) => {
    const compiledField = CompiledField.fromEncField(view, encField);
    return compiledField.hasDateFunc;
  });
  return hierarchyField !== undefined ? CompiledField.fromEncField(view, hierarchyField) : undefined;
}

export function validateDuplicateCalcFields(model: FlamingoTypes.ModeModel, viewName: string, source: string): boolean {
  const columnAttrs = Switch.Attrs.getColumns(model, viewName);
  const calcAttrs = Switch.Attrs.getCalcs(model, viewName);
  const columnNames = columnAttrs.map((attr) => attr.name);
  const calcNames = calcAttrs.map((attr) => attr.name);
  const calcHasDuplicates = calcNames.filter((name) => source === name).length > 1;

  return !(columnNames.includes(source) && calcNames.includes(source)) && !calcHasDuplicates;
}

export function updateMarkColor(enc: Encoding, color: string, series?: string): EncodingResult {
  const encoder = guide.newBatchOperation(enc);
  encoder.updateMarkColor({ value: color }, series);
  return encoder.complete();
}

export function updateMarkColorTarget(enc: Encoding, target: ColorTarget): EncodingResult {
  const encoder = guide.newBatchOperation(enc);
  encoder.updateMarkColor({ target: target });
  return encoder.complete();
}

export function updateMarkOptions(enc: Encoding, markOptions: MarkTypeOptions, series?: string): EncodingResult {
  const encoder = guide.newBatchOperation(enc);
  encoder.updateMarkOptions(markOptions, series);
  return encoder.complete();
}

export function setTextDisplay(
  encoding: Encoding,
  textDisplay: TextDisplay | null | undefined,
  series?: string
): EncodingResult {
  const encoder = guide.newBatchOperation(encoding);
  encoder.setTextDisplay(textDisplay, series);
  return encoder.complete();
}

export function resolveTextDisplay(textDisplay: Readonly<TextDisplay> | undefined | null): Required<TextDisplay> {
  const showAllDisplay = {
    showAll: true,
    showFirst: false,
    showLast: false,
    showMax: false,
    showMin: false,
  };
  const defaultDisplay: Required<TextDisplay> = {
    ...showAllDisplay,
    useLabelThinning: true,
  };
  if (textDisplay == null) {
    return defaultDisplay;
  }
  // Resolve the value for showAll. If it's defined, use it. Otherwise, it is false if any of the other 'show'
  // fields are true.
  const resolvedShowAll =
    textDisplay?.showAll != null
      ? textDisplay.showAll
      : !(textDisplay?.showFirst || textDisplay?.showLast || textDisplay?.showMax || textDisplay?.showMin);

  // Fill any undefined fields
  let resolved = {
    ...defaultDisplay,
    ...textDisplay,
    showAll: resolvedShowAll,
  };

  if (resolved.showAll) {
    // If show all is ON, make sure everything else is OFF
    resolved = { ...resolved, ...showAllDisplay };
  }
  return resolved;
}

export function addVegasField(
  enc: Encoding,
  field: CompiledFormula,
  channel: BasicChannel,
  zone: ZoneType | undefined,
  at?: string
) {
  const encoder = guide.newBatchOperation(enc);
  if (field.isSystem) {
    encoder.addEncField(field.toEncField(), channel, at);
    return encoder.complete();
  } else {
    const defaultEncField: EncField = buildDefaultEncField({ field, channel, zone });
    const encField = encoder.addEncField(defaultEncField, channel, at);
    const encResult = encoder.complete();
    const frame = VegasFrame.makeDefaultExprFrame(encResult.encoding, field, channel);
    return setFrame(encResult.encoding, encField.id ?? '', frame);
  }
}

export function updateOrMoveVegasField(
  result: EncodingResult,
  fieldIds: string[] | string,
  channel: EncChannel | undefined,
  at?: string,
  series?: string,
  formula?: CompiledFormula
) {
  const encoder = guide.newBatchOperation(result.encoding);
  let asContinuousFormula: CompiledFormula | undefined;
  if (channel === 'angle' || channel === 'baseline') {
    asContinuousFormula = attemptToMakeContinuousMeasure(formula, true);
    if (asContinuousFormula == null) {
      // Unsupported add to the angle channel. We throw here but the UI
      // is responsible for blocking this.
      throw new Error('Angle channel only allows continuous fields');
    }
  }
  // If field ids is an array we are moving an all marks field
  if (Array.isArray(fieldIds)) {
    if (channel === 'row' || channel === 'column' || channel === 'value' || channel === 'filter') {
      // moving all marks from mark channel to non mark channel
      const fieldId = fieldIds.shift();
      if (fieldIds.length) {
        encoder.removeFields(fieldIds);
      }

      encoder.updateField({ id: fieldId || '', channel, series, at });
    } else {
      // moving from one marks channel to another
      fieldIds.forEach((fieldId: string, index: number) => {
        const modifyField =
          asContinuousFormula && asContinuousFormula.isField && asContinuousFormula.alias
            ? {
                ...asContinuousFormula.toModifyField(),
                variable: {
                  type: VariableType.CONTINUOUS,
                },
              }
            : {};
        encoder.updateField({
          ...modifyField,
          id: fieldId,
          channel,
          at,
          series: series === '*' ? '*' : result.encoding.marks.series[index].field,
        });
      });
    }
  } else if (channel === 'row' || channel === 'column' || channel === 'value' || channel === 'filter' || at != null) {
    // for non mark channels just update the field
    encoder.updateField({
      id: fieldIds,
      channel,
      at,
      series,
    });
  } else if (formula && channel) {
    if (typeof at === 'string') {
      encoder.removeField(at);
    }
    if (asContinuousFormula && asContinuousFormula.isField && asContinuousFormula.alias) {
      encoder.updateField({
        ...asContinuousFormula.toModifyField(),
        variable: {
          type: VariableType.CONTINUOUS,
        },
      });
    }
    encoder.moveField(fieldIds, channel, undefined, series);
  }
  return encoder.complete();
}

export function buildBasicColumns(encoding: Encoding): ReadonlyArray<EncField | EncDualAxisXField> {
  return buildBasicFields(encoding, 'x');
}

export function buildBasicRows(encoding: Encoding): ReadonlyArray<EncField | EncDualAxisYField> {
  return buildBasicFields(encoding, 'y');
}

function buildRelativeToDualMapping(encoding: Encoding, axis: 'x' | 'y') {
  const dualAxis = axis === 'x' ? 'x2' : 'y2';
  const dualAxisFields = encoding[dualAxis];
  const relToDualMapping = (dualAxisFields as Readonly<EncDualAxisXField>[]).reduce((mappedFields, field) => {
    const refId = getDualRef(field);
    return mapRefToField(refId, field, mappedFields);
  }, new Map<string, EncField[]>());

  return relToDualMapping;
}

function mapRefToField(refId: string, field: EncField, mappedFields: Map<string, EncField[]>) {
  if (!mappedFields.has(refId)) {
    mappedFields.set(refId, []);
  }
  const fields = mappedFields.get(refId);
  fields?.push(field);
  return mappedFields;
}

function getDualRef(field: EncDualAxisXField | EncDualAxisYField) {
  return (field as EncDualAxisXField)?.x ?? (field as EncDualAxisYField)?.y;
}

function buildBasicFields(encoding: Encoding, axis: 'x' | 'y') {
  //
  // There are several possibilities now for dual axis.
  // Dual axis between two fields
  //  y:  EncField field1
  //  y2: EncDualAxisYField field2 which references field1.
  //
  // Dual axis between a field and a shared axis group
  //  y:  EncField field1 in EncFieldGroup group1
  //      EncField field2 in EncFieldGroup group1
  //  y2: EncDualAxisYField field3 which references group1
  //
  // Dual axis between a shared axis group and a field.
  //  y:  EncField field1
  //  y2: EncDualAxisYField field2 in EncFieldGroup group1 which references field1
  //      EncDualAxisYField field3 in EncFieldGroup group1 which references field1
  //
  // Dual axis between a shared axis group and another shared axis group
  //  y:  EncField field1 in EncFieldGroup group1
  //  y:  EncField field2 in EncFieldGroup group1
  //  y2:  EncDualAxisYField field3 in EncFieldGroup group2 which references group1
  //  y2:  EncDualAxisYField field4 in EncFieldGroup group2 which references group1
  //

  //
  // Build a list of fields with the dual axis (x2|y2) fields immediately after the matched
  // relative axis field (x|y). Grouped fields should stay together.

  // This works backward through the relative axis (x|y) fields. If there is a dual (x2|y2)
  // axis field which references this field or field group, then add all of the dual axis
  // fields to the array, then add the relative axis field. We go in reverse order so that the
  // dual axis fields are processed for the **last** group member.

  // The relative-to-dual mapping for the field or group is removed to prevent adding the
  // same matched fields multiple times.

  // This produces a reversed list of fields which is reversed before adding to the row/column
  // fields.

  //  Ex:
  //  Dual axis between a field and a shared axis group
  //  y:  EncField field1 in EncFieldGroup group1
  //      EncField field2 in EncFieldGroup group1
  //      EncField field3 in EncFieldGroup group2
  //  y2: EncDualAxisYField field4 which references group1
  //      EncDualAxisYField field5 which references group2

  //  relativeAxis.reduceRight:
  //    relToDualMapping: {group1: [field4], group2: [field5]}
  //      field3: key = group2
  //      returns [field5, field3]
  //    relToDualMapping: {group1: [field4]}
  //      field2: key = group1
  //      returns [field5, field3, field4, field2]
  //    relToDualMapping: { }
  //      field1: key = group1
  //      returns [field5, field3, field4, field2, field1]

  //  Ex:
  //  Dual axis between a shared axis group and another shared axis group
  //  y:  EncField field1 in EncFieldGroup group1
  //  y:  EncField field2 in EncFieldGroup group1
  //  y2:  EncDualAxisYField field3 in EncFieldGroup group2 which references group1
  //  y2:  EncDualAxisYField field4 in EncFieldGroup group2 which references group1

  //  relativeAxis.reduceRight:
  //      relToDualMapping: { group1: [field3, field4] }
  //      field2: key = group1
  //      returns [field4, field3, field2]
  //      relToDualMapping: { }
  //      field1: key = group1
  //      return [field4, field3, field2, field1]

  const fields: Array<EncField> = axis === 'y' ? [...encoding.row] : [...encoding.column];
  const relToDualMapping = buildRelativeToDualMapping(encoding, axis);

  const relativeAxisFields = encoding[axis].reduceRight((axisFields, field) => {
    const key = field.groupId ?? field.id;
    if (key != null && relToDualMapping.has(key)) {
      axisFields = axisFields.concat((relToDualMapping.get(key) ?? []).reverse());
      relToDualMapping.delete(key);
    }
    axisFields.push(field);
    return axisFields;
  }, [] as Array<EncField>);

  // Add the axis fields in reverse order.
  return fields.concat(relativeAxisFields.reverse());
}

type BasicChannelFields =
  | ReadonlyArray<EncField>
  | ReadonlyArray<EncField | EncDualAxisXField>
  | ReadonlyArray<EncField | EncDualAxisYField>;

function getBasicChannelFields(encoding: Encoding, channel: BasicChannel, onlyDiscretes = false): BasicChannelFields {
  let fields: BasicChannelFields = [];

  switch (channel) {
    case 'column': {
      fields = buildBasicColumns(encoding);
      break;
    }
    case 'row': {
      fields = buildBasicRows(encoding);
      break;
    }
    case 'value': {
      fields = encoding.value;
      break;
    }
    case 'filter': {
      fields = encoding.filter;
      break;
    }
  }

  if (onlyDiscretes) {
    fields = fields.filter((field) => {
      return field.variable.type === VariableType.DISCRETE;
    });
  }

  return fields;
}

export function canAddOrMoveToValues(formula: CompiledFormula | undefined): boolean {
  return prepareForAddOrMoveToValues(formula) != null;
}

export function prepareForAddOrMoveToValues(formula: CompiledFormula | undefined): CompiledFormula | undefined {
  return attemptToMakeContinuousMeasure(formula);
}

export function canBeContinuousMeasure(formula: CompiledFormula | undefined, allowValues: boolean) {
  return attemptToMakeContinuousMeasure(formula, allowValues) != null;
}

export function attemptToMakeContinuousMeasure(
  formula: CompiledFormula | undefined,
  allowValues = false
): CompiledFormula | undefined {
  // Bail on empty and invalid fields
  if (formula == null || formula.isNames || !formula.isValid) {
    return;
  }

  // Bail if not allowing measure values
  if (formula.isValues && !allowValues) {
    return;
  }

  // Bail if we can't get to the attr
  if (formula.attrName == null) {
    return;
  }

  ///
  /// Adding

  if (!formula.isField || formula.alias == null) {
    // No valid representation for this on drop
    if (formula.isNonScalarCalc && !formula.isFormulaNumeric) {
      return undefined;
    }
    // Will be aggregated on add if necessary
    // see: buildDefaultEncField
    else {
      return formula;
    }
  }

  ///
  /// Moving

  // Non Scalar Calc
  // ---
  // * Allowed if numeric
  if (formula.isNonScalarCalc) {
    if (formula.isFormulaNumeric) {
      return asContinuousMeas(formula);
    }
    // There's no formula we can create to make this
    // a continuous measure. For example, the following
    // non-scalar calc
    //
    // if avg(rating) > 8 then 'great'
    // elseif avg(rating) > 5 then 'good'
    // elseif avg(rating) > 2 then 'meh'
    // else 'nope'
    // end
    //
    else {
      return;
    }
  }

  // Known-form numeric agg func or quick calc?
  if (formula.hasAggFunc || formula.hasAnalyticFunc) {
    if (formula.isFormulaNumeric) {
      return asContinuousMeas(formula);
    }
  }

  // Has default agg func
  // ---
  // * Allowed if it's valid as a continuous field
  const aggFn = formula.defaultAggregate ?? '';
  if (ExprCall.Name.isKnown(aggFn)) {
    const source = FormulaOperations.aggregate(formula, aggFn as ExprCall.Name);
    return asContinuousMeas(formula, source);
  }

  ///
  ///

  /**
   * If the formula cannot be validly represented as a continuous measure
   * we bail and return undefined. If the formula is a continuous measure
   * it is returned as-is. Otherwise, we'll attempt to re-compile:
   *
   * 1. Optionally with a new formula source
   * 2. As a continuous measure
   */
  function asContinuousMeas(compiled: CompiledFormula, source = compiled.formula) {
    // Formula source is unchanged, use formula as-is if possible
    if (source === compiled.formula) {
      if (compiled.isContinuous && compiled.isFormulaNumeric) {
        if (!compiled.isAggregateable) {
          return compiled;
        }
      }
    }
    // Requires new source, type conversion, or both
    const formula = compiled.withNewFormula(source, Attr.Type.Continuous);
    if (formula.isValid && formula.isContinuous && formula.isFormulaNumeric) {
      if (!formula.isAggregateable) {
        return formula;
      }
    }
  }
}

export function addOrMoveVegasFieldToBasicChannel(
  view: FlamingoTypes.ModeView,
  encoding: Encoding,
  channel: BasicChannel,
  formula?: CompiledFormula,
  position?: number,
  // How do we construct this .. not an ideal arg
  fieldIds?: string[] | string,
  zoneType?: ZoneType
): EncodingResult | undefined {
  // validate formula
  if (formula == null) {
    return;
  }

  // Validate channel
  switch (channel) {
    case 'column':
    case 'row': {
      // if a zone type is passed in we want to set the type to the field
      // Update in encoding before moving
      // only done on move
      if (formula.isField && formula.alias && zoneType) {
        //if we have a zone type we need to also convert it to variable type
        ({ encoding } = updateVegasFieldEncoding(view, encoding, {
          ...formula.toModifyField(),
          variable: {
            type: zoneType === ZoneType.Aggregate ? VariableType.CONTINUOUS : VariableType.DISCRETE,
          },
        }));
      }
      break;
    }
    case 'value': {
      formula = prepareForAddOrMoveToValues(formula);
      if (formula == null) {
        return;
      }

      // Update in encoding before moving
      if (formula.isField && formula.alias) {
        ({ encoding } = updateVegasFieldEncoding(view, encoding, {
          ...formula.toModifyField(),
          variable: {
            type: VariableType.CONTINUOUS,
          },
        }));
      }

      break;
    }
    default: {
      return;
    }
  }

  /**
   * @description Alias of the EncField being moved from one channel to another.
   * If a new field is added to the encoding this will be undefined
   */
  const fieldAlias = formula.isField ? formula.alias || void 0 : void 0;

  /**
   * @description Fields of the target channel including dual axis pairs, if any
   */
  let fields = getBasicChannelFields(
    encoding,
    channel,
    channel === 'value' || formula.isFormulaDate ? false : formula.isDiscrete
  );

  /**
   * @description Boolean indicating whether or not the field already resides in
   * the target channel. For instance, when moving a series up or down relative
   * to another series
   */
  let fieldResidesInChannel = false;

  /**
   * @description If the field being placed exists in the target channel then
   * we are _moving_ the field, possibly to a new location, within its current
   * channel. If this is the case then set the fields pre-move index within the
   * channel. If the field is being added to the encoding for the first time,
   * or moved to a new channel, this will be NaN
   */
  let fieldIndex = NaN;

  if (fieldAlias != null) {
    fieldResidesInChannel = fields.some((field) => {
      return fieldAlias === field.id;
    });

    if (fieldResidesInChannel) {
      fieldIndex = fields.findIndex((field) => {
        return fieldAlias === field.id;
      });
    }
  }

  /**
   * @description Desired position of the field in the target channel, clamped to
   * fit within the bounds of said channel
   */
  position = Math.max(0, Math.min(position ?? fields.length, fields.length));

  /**
   * @description The adjusted position accounts for a field being repositioned
   * within its current channel. When a field is moved DOWN, it has to hop over
   * itself. The position we want to normalize in this scenario is the index
   * right _after_ the field being moved. Por ejemplo:
   *
   * ~~ 0 ~~
   * [MONTH(some_date)]
   * ~~ 1 ~~
   * [YEAR(some_date)]
   * ~~ 2 ~~
   *
   * 1. Grab [MONTH(some_date)] and drag it UP one tick, you're at index 0:
   * fields[0] === [MONTH(some_date)] // true. No work to be done
   *
   * 2. Grab [MONTH(some_date)] and drag it DOWN one tick, you're at index 1:
   * fields[1] === [MONTH(some_date)] // false. We _think_ there's work to be done
   *
   * It's case #2 we're accommodating here. Normalize the indexes around the moved
   * field so we know when work need not be done. In any other case the position
   * can be used as-is
   */
  const adjustedPosition =
    fieldResidesInChannel && fieldIndex < position
      ? // Moving the field over itself (DOWN)
        Math.max(position - 1, 0)
      : // - Moving a field UP
        // - Moving a field to a different channel
        // - Adding a new field to the encoding
        position;

  // If the field is part of a group and we are moving to 'column' or 'row' then
  // just move field out of shared axis
  const encField = fieldAlias != null ? getEncFieldById(encoding, fieldAlias) : undefined;
  if (encField?.groupId != null && (channel === 'column' || channel === 'row')) {
    return SharedAxisHelpers.moveFieldOutOfSharedAxis(encoding, encField, channel, adjustedPosition);
  }

  // Bail if we didn't move the field.
  if (fieldIndex === adjustedPosition) {
    return;
  }

  ///////
  ///////

  let encResult = loadEncoding(encoding);

  /**
   * Check if we're splitting a dual axis pair by placing a field between one.
   * This is new behavior and is applicable to both adding or moving a field
   */
  if (formula.isContinuous) {
    const field = fields[position];

    // Appease the type system
    if (typeof field?.id === 'string') {
      if (isSecondOfDualAxisPair(encResult.encoding, field.id)) {
        // Split dual axis pair and reset the fields
        if (field.groupId != null) {
          encResult = SharedAxisHelpers.toggleDualAxisOnSharedAxis(encResult.encoding, field.groupId);
        } else {
          encResult = toggleDualAxisField(encResult.encoding, field.id);
        }
        fields = getBasicChannelFields(encResult.encoding, channel);
      }
    }
  }

  // Adding
  // //////

  if (fieldAlias == null) {
    // If the field is a date and is being added before a discrete field,
    // ensure that the zoneType is discrete.
    // Otherwise, it will always get added to the end of the discrete fields.
    //
    if (formula.isFormulaDate && zoneType === undefined && position < fields.length) {
      // Get the field after the position from fields
      const nextField = fields[position];
      if (nextField.variable.type === VariableType.DISCRETE) {
        zoneType = ZoneType.Discrete;
      }
    }
    return addVegasField(encResult.encoding, formula, channel, zoneType, fields[position]?.id);
  }

  // Moving
  // //////

  let invokeMoveRoutine = formula.isContinuous && fieldResidesInChannel;
  let reCreateDualAxisRef: string | undefined;

  /**
   * If we're _moving_ a continuous field, and said field is part of a dual axis
   * pair.. there is more work to be done. If the field if being moved to a contiguous
   * position around the pair then we'll need the channel and original position;
   * there's special handling in the encoder for reordering dual axis pairs.
   *
   * If the field is one of a dual axis pair and it is being moved further
   * away, then we need to split the pair before moving the field
   */
  if (invokeMoveRoutine) {
    // Appease type system
    if (typeof fieldAlias !== 'string') {
      throw new Error('Attempted to move a field without an alias');
    }

    const isFirstOfPair = isFirstOfDualAxisPair(encResult.encoding, fieldAlias);
    const isSecondOfPair = isSecondOfDualAxisPair(encResult.encoding, fieldAlias);

    // Moving one of a dual axis pair
    if (isFirstOfPair || isSecondOfPair) {
      const sizeOfFieldOrGroup = (fieldOrGroup: EncField | undefined) => {
        if (fieldOrGroup === undefined || fieldOrGroup.groupId == null) {
          return 1;
        }
        return fields.filter((f) => f.groupId === fieldOrGroup.groupId).length;
      };

      let lo = isFirstOfPair ? fieldIndex : fieldIndex - sizeOfFieldOrGroup(fields[fieldIndex - 1]);
      let hi = isFirstOfPair ? fieldIndex + sizeOfFieldOrGroup(fields[fieldIndex + 1]) : fieldIndex;

      // Moving a dual axis pair further than one of the contiguous indexes..
      // we need to invoke the move routine in the encoder, but bypass the
      // dual axis handling; split the pair before moving the field
      if (adjustedPosition < lo || adjustedPosition > hi) {
        const dualAxisField = fields[hi];

        if (dualAxisField?.groupId != null) {
          encResult = SharedAxisHelpers.toggleDualAxisOnSharedAxis(encResult.encoding, dualAxisField.groupId);
          fields = getBasicChannelFields(encResult.encoding, channel);
        } else if (typeof dualAxisField?.id === 'string') {
          // Split dual axis pair and reset the fields
          encResult = toggleDualAxisField(encResult.encoding, dualAxisField.id);
          fields = getBasicChannelFields(encResult.encoding, channel);
        }
      }
      // Still moving the field, but! the encoder correctly handles reordering
      // dual axis pairs and requires that we provide a channel and the original
      // position to do so
      // see: https://github.com/mode/vegas/blob/master/source/Guide/Encoder.ts#L1563
      else {
        invokeMoveRoutine = false;
      }
    }
  }

  /**
   * If we're still _moving_ the field we must check to see if we're placing it
   * immediately after [another] dual axis pair.. this necessitates:
   *
   * 1. Splitting the pair
   * 2. Moving the field
   * 3. Re-joining the pair
   */
  if (invokeMoveRoutine) {
    const field = fields[position - 1];
    if (typeof field?.id === 'string') {
      if (isSecondOfDualAxisPair(encResult.encoding, field.id)) {
        encResult = toggleDualAxisField(encResult.encoding, field.id);
        reCreateDualAxisRef = field.id;
      }
    }
  }

  const alias = fieldAlias as string;
  encResult = updateOrMoveVegasField(
    encResult,
    fieldIds ?? alias,
    // Target channel
    invokeMoveRoutine
      ? /**
         * Moving a continuous field within its current channel requires that we
         * OMIT `channel` from this call, which invokes the move routine in the
         * encoder. This is necessary for, say, reordering a series. If we remove
         * then add the series, we lose all the associated marks. No good
         */
        undefined
      : /**
         * Always provide the channel otherwise:
         *
         * 1. Moving a continuous field to a new channel. If it IS a series
         * then we need to remove its associated marks, which the encoder will
         * do for us when removing -> adding a field
         *
         * 2. Moving a discrete field. We MUST have the encoder remove -> add
         * the field, as is does not support moving a field when its target
         * ref has a different parent, ie. between { column, x }: same logical
         * grouping, technically different channels
         * see: https://github.com/mode/vegas/blob/master/source/Guide/Encoder.ts#L1877
         *
         * 3. Reordering a dual axis pair. The encoder will preserve the series
         * marks when swapping the fields
         */
        channel,
    // Placement ref
    invokeMoveRoutine
      ? /**
         * The encoder's move routine MUST be given the adjusted index...
         * see: https://github.com/mode/vegas/blob/master/source/Guide/Encoder.ts#L609
         * and: mode/muze-issues#734
         */
        fields[adjustedPosition]?.id
      : /**
         * While the remove -> add routine MUST be given the original position..
         * see: https://github.com/mode/vegas/blob/master/source/Guide/Encoder.ts#L550-L596
         */
        fields[position]?.id
  );

  /**
   * Lastly, re-join a dual axis pair if we had to
   * split it before placing the field
   */
  if (typeof reCreateDualAxisRef === 'string') {
    encResult = toggleDualAxisField(encResult.encoding, reCreateDualAxisRef);
  }

  return encResult;
}

export function addOrReplaceVegasField(
  result: EncodingResult,
  field: CompiledFormula,
  channel: MarkChannel,
  series?: string,
  zone?: ZoneType
) {
  const encoding = result.encoding;
  const encoder = guide.newBatchOperation(encoding);
  if (field.isSystem) {
    encoder.addEncFieldToSeries(field.toEncField(), channel, series);
  } else {
    const frame = VegasFrame.makeDefaultExprFrame(encoding, field, channel);
    encoder.addEncFieldToSeries(buildDefaultEncField({ field, channel, frame, zone }), channel, series);
  }
  return encoder.complete();
}

export function replaceAndAddNewVegasField(
  encoding: Encoding,
  newField: CompiledFormula,
  fieldIds: string[] | string,
  channel: EncChannel,
  series?: string,
  zone?: ZoneType
) {
  let encoder = guide.newBatchOperation(encoding);
  if (newField.isSystem) {
    if (channel === 'row' || channel === 'column' || channel === 'value' || channel === 'filter') {
      encoder.addEncField(newField.toEncField(), channel, Array.isArray(fieldIds) ? fieldIds[0] : fieldIds);
    } else {
      encoder.addEncFieldToSeries(
        newField.toEncField(),
        channel,
        series,
        Array.isArray(fieldIds) ? fieldIds[0] : fieldIds
      );
    }
  } else if (channel === 'row' || channel === 'column' || channel === 'value' || channel === 'filter') {
    const encField = encoder.addEncField(
      buildDefaultEncField({ field: newField, zone }),
      channel,
      Array.isArray(fieldIds) ? fieldIds[0] : fieldIds
    );

    let encResult = encoder.complete();
    const frame = VegasFrame.makeDefaultExprFrame(encResult.encoding, newField, channel);
    encResult = setFrame(encResult.encoding, encField.id ?? '', frame);
    encoder = guide.newBatchOperation(encResult.encoding);

    const isDiscrete = encField.variable.type === VariableType.DISCRETE;
    if (isDiscrete && encField.id && channel !== 'filter' && typeof fieldIds === 'string') {
      // Find nested sort of the field we're swapping out, if any
      const encSort = encResult.encoding.sorts
        .filter((sort): sort is EncSort.ByNested => sort.type === SortType.NESTED)
        .find((sort) => sort.field === fieldIds);

      // New field is discrete, formula did not change.. if the previous field
      // had a nested sort we need to (re-)apply it to the new field
      if (encField.formula === encSort?.source) {
        const field = encField.id;
        encoder.addSort({
          ...encSort,
          field,
        });
      }
    }
  } else {
    const frame = VegasFrame.makeDefaultExprFrame(encoding, newField, channel);
    encoder.addEncFieldToSeries(
      buildDefaultEncField({ field: newField, channel, frame, zone }),
      channel,
      series,
      Array.isArray(fieldIds) ? fieldIds[0] : fieldIds
    );
  }
  if (Array.isArray(fieldIds)) {
    encoder.removeFields(fieldIds);
  } else {
    encoder.removeField(fieldIds);
  }
  return encoder.complete();
}

export function replaceAndMoveVegasField(
  encoding: Encoding,
  fieldIds: string[] | string,
  channel: EncChannel,
  oldFieldIds: string[] | string,
  series?: string,
  formula?: CompiledFormula,
  channelZone?: ZoneType
): EncodingResult {
  const encoder = guide.newBatchOperation(encoding);

  let asContinuousFormula: CompiledFormula | undefined;
  if (channel === 'angle' || channel === 'baseline') {
    asContinuousFormula = attemptToMakeContinuousMeasure(formula, true);
    if (asContinuousFormula == null) {
      // Unsupported add to the angle channel.
      throw new Error('Angle channel only allows continuous fields');
    }
  }

  if (Array.isArray(oldFieldIds)) {
    // Case 1: Replacing an all mark field
    if (Array.isArray(fieldIds)) {
      // Part A: Moving an all mark field
      fieldIds.forEach((fieldId: string, index: number) => {
        const modifyField =
          asContinuousFormula && asContinuousFormula.isField && asContinuousFormula.alias
            ? {
                ...asContinuousFormula.toModifyField(),
                variable: {
                  type: VariableType.CONTINUOUS,
                },
              }
            : {};
        encoder.updateField({
          ...modifyField,
          id: fieldId,
          channel,
          at: oldFieldIds[index],
          series: series === '*' ? '*' : encoding.marks.series[index].field,
        });
      });
      encoder.removeFields(oldFieldIds);
    } else if (formula && channel !== 'row' && channel !== 'column' && channel !== 'value' && channel !== 'filter') {
      // Part B: moving a basic channel field
      encoder.removeFields(oldFieldIds);
      if (asContinuousFormula && asContinuousFormula.isField && asContinuousFormula.alias) {
        encoder.updateField({
          ...asContinuousFormula.toModifyField(),
          variable: {
            type: VariableType.CONTINUOUS,
          },
        });
      }
      encoder.moveField(fieldIds, channel, undefined, series);
    }
  } else if (channel === 'row' || channel === 'column' || channel === 'value' || channel === 'filter') {
    if (channel === 'value') {
      formula = prepareForAddOrMoveToValues(formula);

      // Update in encoding before moving
      if (formula?.isField && formula.alias) {
        encoder.updateField({
          ...formula.toModifyField(),
          variable: {
            type: VariableType.CONTINUOUS,
          },
        });
      }
    }
    // Case 2: replacing a basic channel field
    if (Array.isArray(fieldIds)) {
      // Part A: moving an all mark field
      const fieldId = fieldIds.shift();
      if (fieldId) {
        if (fieldIds.length) {
          encoder.removeFields(fieldIds);
        }
        encoder.updateField({ id: fieldId, channel, series, at: oldFieldIds });
        encoder.removeField(oldFieldIds);
      }
    } else {
      // Part B: moving a series mark field
      // Part C: moving a basic channel field
      encoder.updateField({ id: fieldIds, channel, at: oldFieldIds, series });
      encoder.removeField(oldFieldIds);
    }
  } else if (formula) {
    // Case 3: replacing a series mark field
    if (Array.isArray(fieldIds)) {
      // Part A: moving an all mark field
      // Not Currently Available in the UI
    } else {
      // Part B: moving a series mark field
      // Part C: moving a basic channel field
      encoder.removeField(oldFieldIds);
      if (asContinuousFormula && asContinuousFormula.isField && asContinuousFormula.alias) {
        encoder.updateField({
          ...asContinuousFormula.toModifyField(),
          variable: {
            type: VariableType.CONTINUOUS,
          },
        });
      }
      if (channelZone != null && formula.isField) {
        const defaultField = buildDefaultEncField({ field: formula, zone: channelZone, channel });
        encoder.updateField({
          ...formula.toModifyField(),
          ...defaultField,
        });
      }
      encoder.moveField(fieldIds, channel, undefined, series);
    }
  }

  return encoder.complete();
}

export function replaceQCMeasureNamesField(
  encoding: Encoding,
  field: CompiledField,
  channel: EncChannel,
  zone: ZoneType
) {
  const encoder = guide.newBatchOperation(encoding);

  const series = encoding.marks.series.length ? undefined : '*';

  encoder.updateField({
    ...buildUpdateFieldParams(field, zone),
    channel: channel,
    series: encoding.marks.series.length ? encoding.marks.series[0].field : series,
  });

  return encoder.complete();
}

export function removeFieldsFromEncoding(encoding: Encoding, fieldIds: string[]) {
  const encoder = guide.newBatchOperation(encoding);
  encoder.removeFields(fieldIds);
  return encoder.complete();
}

export function removeAllColorsFromEncoding(encoding: Encoding): EncodingResult | undefined {
  const colorFields = getUniqueEncFieldsByMarkChannel(encoding, 'color');
  if (!colorFields.length) {
    return;
  }

  let allFieldsHaveId = true;
  let encoder: BatchEncoder | undefined;

  colorFields.forEach((field) => {
    if (field?.id) {
      if (!encoder) {
        encoder = guide.newBatchOperation(encoding);
      }
      encoder.removeField(field.id);
      encoder.replaceMarkColor({ value: undefined }, field.id);
    } else {
      allFieldsHaveId = false;
    }
  });

  if (!allFieldsHaveId || !encoder) {
    return;
  } else {
    encoder.replaceMarkColor({ value: undefined });
    return encoder.complete();
  }
}

export function removeFieldFromEncoding(encoding: Encoding, chartType: ChartTypes.ChartType, fieldId: string) {
  const encoder = guide.newBatchOperation(encoding);
  encoder.removeField(fieldId);

  switch (chartType) {
    // pies and donuts don't have to have a color picked
    case 'donut':
    case 'pie':
    case 'linePlusBar':
    case 'bigValue':
    case 'vegas':
    case 'vegasPivotTable':
      break;
    default:
      if (
        !encoding.marks.series.length ||
        !encoding.marks.series[0].properties.color.values.length ||
        encoding.marks.series[0].properties.color.values[0].id === fieldId
      ) {
        encoder.addEncFieldToSeries({ ...NAMES_FIELD }, 'color');
      }
      break;
  }

  return encoder.complete();
}

/**
 * Returns the proper formula based on field datatype, channel, and zone
 * @param {BuildDefaultEncField} buildDefaultEncField - BuildDefualtEncField
 * @property {Object} buildDefaultEncField.field     - CompiledFormula
 * @property {string} [buildDefaultEncField.zone]    - ZoneType
 * @property {string} [buildDefaultEncField.channel] - EncChannel
 * @returns {EncField} EncField
 */
export function buildDefaultEncField(buildDefaultEncField: BuildDefaultEncField): EncField {
  const { field, channel, frame } = buildDefaultEncField;
  let { zone } = buildDefaultEncField;

  if (!zone) {
    switch (channel) {
      case 'row':
      case 'column':
        zone = field.isAttrDateType
          ? ZoneType.Continuous
          : field.varType === Attr.Type.Discrete
            ? ZoneType.Discrete
            : ZoneType.Aggregate;
        break;
      case 'value':
        zone = ZoneType.Aggregate;
        break;
      case 'angle':
      case 'size':
        zone =
          !field.isNonScalarCalc || field.isAttrNumberType
            ? ZoneType.Aggregate
            : field.varType === Attr.Type.Discrete
              ? ZoneType.Discrete
              : ZoneType.Aggregate;
        break;
      case 'baseline':
        zone = field.isFormulaDate
          ? ZoneType.Continuous
          : !field.isNonScalarCalc || field.isAttrNumberType
            ? ZoneType.Aggregate
            : ZoneType.Continuous;
        break;
      default:
        zone = field.varType === Attr.Type.Discrete ? ZoneType.Discrete : ZoneType.Aggregate;
        break;
    }
  }

  if (field.isValid) {
    const attrName = escapeAttrName(field.attrName || '');

    switch (field.dataType) {
      case DataType.Name.Boolean:
      case DataType.Name.String:
        switch (zone) {
          case ZoneType.Aggregate: {
            let formula = `[${attrName}]`;
            let varType = VariableType.DISCRETE;
            if (field.isAggregateable) {
              formula = FormulaOperations.aggregate(field, field.defaultAggregate ?? ExprCall.Name.Count);
              varType = VariableType.CONTINUOUS;
            }
            return {
              formula,
              frame,
              variable: {
                type: varType,
              },
            };
          }
          case ZoneType.Continuous:
          case ZoneType.Discrete:
            return {
              formula: `[${attrName}]`,
              variable: { type: VariableType.DISCRETE },
              frame,
            };
          default: {
            throw new Error(`Unexpected ZoneType<${zone}> for DataType<${field.dataType}>`);
          }
        }
      case DataType.Name.DateTime:
      case DataType.Name.Timestamp:
        switch (zone) {
          case ZoneType.Aggregate:
            return {
              formula: field.isAggregateable
                ? FormulaOperations.aggregate(field, field.defaultAggregate ?? ExprCall.Name.Count)
                : `[${attrName}]`,
              variable: { type: VariableType.CONTINUOUS },
              frame,
            };
          case ZoneType.Continuous:
            return {
              formula: `DATETRUNC('${DateTime.Unit.Day}', [${attrName}])`,
              variable: { type: VariableType.CONTINUOUS },
              frame,
            };
          case ZoneType.Discrete:
            return {
              formula: `DATEPART('${DateTime.Unit.Year}', [${attrName}])`,
              variable: { type: VariableType.DISCRETE },
              frame,
            };
          default: {
            throw new Error(`Unexpected ZoneType<${zone}> for DataType<${field.dataType}>`);
          }
        }
      case DataType.Name.Integer:
      case DataType.Name.Float:
      case DataType.Name.Decimal:
        switch (zone) {
          case ZoneType.Aggregate: {
            return {
              formula: field.isAggregateable
                ? // Semantically, dimension vs. measure matters here. Use default aggregate if set
                  FormulaOperations.aggregate(field, field.defaultAggregate ?? ExprCall.Name.Sum)
                : `[${attrName}]`,
              variable: { type: VariableType.CONTINUOUS },
              frame,
            };
          }
          case ZoneType.Continuous:
            return {
              formula: `[${attrName}]`,
              variable: { type: VariableType.CONTINUOUS },
              frame,
            };
          case ZoneType.Discrete:
            return {
              formula: `[${attrName}]`,
              variable: { type: VariableType.DISCRETE },
              frame,
            };
          default: {
            throw new Error(`Unexpected ZoneType<${zone}> for DataType<${field.dataType}>`);
          }
        }
    }
  }

  return {
    formula: field.formula,
    variable: { type: zone === ZoneType.Aggregate ? VariableType.CONTINUOUS : VariableType.DISCRETE },
    frame,
  };
}

export function getEncSeriesMarkById(encoding: Encoding, field: string): MarkType | undefined {
  if (!encoding) {
    return;
  }

  if (encoding.marks.series) {
    const series = encoding.marks.series.find((s) => s.field === field);
    return series ? series.properties.type.value : undefined;
  }
}

export function isDualAxis(encoding: Encoding, axis: 'x' | 'y') {
  if (encoding != null) {
    return axis === 'y'
      ? encoding.y2.some((y2) => encoding.y.some((y) => y2.y === y.id))
      : encoding.x2.some((x2) => encoding.x.some((x) => x2.x === x.id));
  }
  return false;
}

export function isDualAxisEncoding(encoding: Encoding, field: string) {
  return isSecondOfDualAxisPair(encoding, field);
}

export function isOneOfDualAxisPair(encoding: Encoding, id: string) {
  if (encoding != null) {
    return (
      encoding.x2.some((x2: EncDualAxisXField) => x2.id === id || x2.x === id) ||
      encoding.y2.some((y2: EncDualAxisYField) => y2.id === id || y2.y === id)
    );
  }
  return false;
}

export function isFirstOfDualAxisPair(encoding: Encoding, id: string) {
  if (encoding != null) {
    return (
      encoding.x2.some((x2: EncDualAxisXField) => x2.x === id) ||
      encoding.y2.some((y2: EncDualAxisYField) => y2.y === id)
    );
  }
  return false;
}

export function isSecondOfDualAxisPair(encoding: Encoding, id: string) {
  if (encoding != null) {
    return (
      encoding.x2.some((x2: EncDualAxisXField) => x2.id === id) ||
      encoding.y2.some((y2: EncDualAxisYField) => y2.id === id)
    );
  }
  return false;
}

export function supportsColorAsBackground(encoding: Encoding, field: EncField | undefined): boolean {
  // Only for tabular. Hopefully this can be removed someday.
  if (encoding.marks.channel !== '*') {
    return false;
  }
  // Only for text
  if (encoding.marks.properties.type.value !== MarkType.Text) {
    return false;
  }
  // Only if field is null or matches the color
  return field == null || encoding.marks.properties.color.values[0]?.id === field?.id;
}

export function supportsBaselineChannel(markType: MarkType | undefined) {
  return markType && [MarkType.Area, MarkType.Bar].includes(markType);
}

export function getUniqueEncFieldsByMarkChannel(encoding: Encoding, channel: MarkChannel): EncField[] {
  if (!encoding) {
    return [];
  }
  const channelFields: EncField[] = [];

  // Add all series color channel values that are unique by formula and discrete/continuous
  encoding.marks.series.forEach((series: EncSeries) => {
    series.properties[channel].values?.forEach((channelField: EncField) => {
      if (
        !channelFields.find(
          (field: EncField) =>
            field.formula === channelField.formula && field.variable.type === channelField.variable.type
        )
      ) {
        channelFields.push(channelField);
      }
    });
  });

  encoding.marks.properties[channel].values?.forEach((channelField: EncField) => {
    if (
      !channelFields.find(
        (field: EncField) =>
          field.formula === channelField.formula && field.variable.type === channelField.variable.type
      )
    ) {
      channelFields.push(channelField);
    }
  });

  return channelFields;
}

export function isEncodingEmpty(encoding: Encoding): boolean {
  if (!encoding) return true;
  return !collectEncFields(encoding).some((f) => {
    return f.formula !== Names.Key && f.formula !== Values.Key;
  });
}

export function isInEncoding(encoding: Encoding, formula: string): boolean {
  if (!encoding) return false;
  return collectEncFields(encoding).some((f) => {
    return f.formula === formula;
  });
}

/**
 * Finds the first field in the encoding that matching the specified formula. Note: this does not search
 * sorts or filters.
 * @param encoding
 * @param formula
 * @returns EncField or undefined
 */
export function findInEncoding(encoding: Encoding, formula: string) {
  if (encoding) {
    return collectEncFields(encoding).find((f) => {
      return f.formula === formula;
    });
  }
}

/**
 * @description Returns an array of the applicable EncFields of an Encoding optionally filtered by series
 */
export function collectEncFields(enc: Encoding, series?: '*' | string): ReadonlyArray<EncField> {
  if (!enc) return [];

  /**
   * If `series` is provided, filter continuous fields out of the primary set
   * of fields that are not the series. If no series is provided, all fields
   * are returned
   */
  const filterPrimary = (fields: readonly EncField[], series?: string) => {
    if (series) {
      return fields.filter((f) => {
        return f.variable.type === VariableType.DISCRETE || f.id === series;
      });
    }
    return fields;
  };

  const encSeries: EncSeries[] = [];
  const encFields: EncField[] = [];

  switch (enc.marks.channel) {
    case '*': {
      // Validate optional series
      if (series && series !== '*') {
        return [];
      }

      // Push column|x|x2
      encFields.push(...buildBasicColumns(enc));
      // Push row|y|y2
      encFields.push(...buildBasicRows(enc));
      // Push root marks
      encSeries.push({
        properties: enc.marks.properties,
        field: '*',
      });
      break;
    }
    case 'x': {
      // Validate optional series
      if (
        series &&
        !enc.marks.series.some((encSeries) => {
          return encSeries.field === series;
        })
      ) {
        return [];
      }

      // Push optionally filtered column|x|x2
      encFields.push(...filterPrimary(buildBasicColumns(enc), series));
      // Push row|y|y2
      encFields.push(...buildBasicRows(enc));
      // Push series or all-series marks
      encSeries.push(
        ...enc.marks.series.filter((encSeries) => {
          return series === undefined || encSeries.field === series;
        })
      );
      break;
    }
    case 'y': {
      // Validate optional series
      if (
        series &&
        !enc.marks.series.some((encSeries) => {
          return encSeries.field === series;
        })
      ) {
        return [];
      }

      // Push column|x|x2
      encFields.push(...buildBasicColumns(enc));
      // Push optionally filtered row|y|y2
      encFields.push(...filterPrimary(buildBasicRows(enc), series));
      // Push series or all-series marks
      encSeries.push(
        ...enc.marks.series.filter((encSeries) => {
          return series === undefined || encSeries.field === series;
        })
      );
      break;
    }
  }

  // Point at which we'll splice in Measure Values
  const valuesIndex = encFields.length;

  // Push (*|series) marks
  for (const series of encSeries) {
    encFields.push(
      ...series.properties.color.values,
      ...series.properties.size.values,
      ...series.properties.text.values,
      ...series.properties.detail.values,
      ...series.properties.label.values,
      ...series.properties.angle.values,
      ...series.properties.tooltip.values,
      ...series.properties.baseline.values
    );
  }

  // Push values if Measure Values is in the encoding
  const hasMeasureValues = encFields.some((f) => f.formula === Values.Key);
  if (hasMeasureValues) {
    encFields.splice(valuesIndex, 0, ...enc.value);
  }

  return encFields;
}

type FrameValidationResult =
  // Valid: error is undefined
  | { valid: true; error?: undefined }
  // Invalid: error message is required
  | { valid: false; error: string };

/**
 * @description Validates fields with an {@link ExprFrame}. Fields _without_ a frame
 * are considered valid as are non-analytic fields _with_ a frame. Custom frames require
 * actual work to validate; a custom frame is determined to be valid if its addressing
 * dimensions 1) exist within the scope of a field and 2) are in fact dimensions. For info
 * on determining which fields are "in scope", see {@link getCommonFieldsInScopeOfField}
 */
export function validateEncodingFieldFrame(
  field: EncField,
  encoding: Encoding,
  schemaModel: SchemaModel
): FrameValidationResult {
  /**
   * @description Returns `true` if the field is a dimension, `false` otherwise
   */
  const isDimension = (field?: FlamingoTypes.ModeField): field is FlamingoTypes.ModeField =>
    field?.source === Attr.Source.Field && field.role === Attr.Role.Dimension;

  ///
  ///

  if (field.frame?.type !== ExprFrame.Type.Custom) {
    return { valid: true };
  }

  const fieldId = field.id as string;
  const formulas = field.frame.address;

  if (formulas.length > 0) {
    // Opting to not fail in this case.. we might have a stranded frame
    // on the field, but frames will not affect non-analytic fields ..
    const compiled = schemaModel.compileEncField(field);
    if (!(compiled.isValid && compiled.isAnalyticCalc)) {
      return { valid: true };
    }

    // Validate the frame formulas..
    // - Must be a valid formula
    // - Must be a Field (no MN/MV)
    // - Must be a dimension

    const frameFields: FlamingoTypes.ModeField[] = [];
    for (const source of formulas) {
      const field = schemaModel.buildField(source, VariableType.DISCRETE, undefined);
      const fieldName = field?.name ?? source;
      if (!isDimension(field)) {
        return {
          error:
            `The table calculation is missing a required field. To fix this, ` +
            `add "${fieldName}" to the visualization`,
          valid: false,
        };
      } else {
        frameFields.push(field);
      }
    }

    // Frame formulas must be in scope of the target field..
    // - Collect fields of the encoding that are in scope of the field
    //   and common (by formula) to _all series_
    // - Ensure each frame dimension can be found in this set

    const fields = getCommonFieldsInScopeOfField(encoding, fieldId);
    const dimensions = uniqBy(fields, 'formula')
      .map((field) => schemaModel.makeField(field))
      .filter(isDimension);

    for (const frameDim of frameFields) {
      const isInScope = dimensions.some((dim) => {
        return dim.formula.source === frameDim.formula.source;
      });
      if (!isInScope) {
        return {
          error:
            `The table calculation is missing a required field. To fix this, ` +
            `add "${frameDim.name}" to the visualization`,
          valid: false,
        };
      }
    }
  }

  return {
    valid: true,
  };
}

/**
 * Sets a default frame for any analytic calc field which does
 * not already have one. This function is intended to be used
 * with Quick Charts encodings which have very limited set of
 * locations where an analytic field can be placed.
 */
export function setMissingAnalyticFrames(encoding: Encoding, model: SchemaModel) {
  const encFields = collectEncFields(encoding);
  let encResult = loadEncoding(encoding);
  encFields.forEach((field) => {
    const compiled = model.compileEncField(field);
    if (compiled.isAnalyticCalc && field.id != null && field.frame === undefined) {
      // Not passing the channel here as it is not relevent for any Quick Charts
      // encodings.
      const defaultFrame = VegasFrame.makeDefaultExprFrame(encResult.encoding, compiled);
      encResult = setFrame(encResult.encoding, field.id, defaultFrame);
    }
  });
  return encResult.encoding;
}

/**
 * @description Returns fields that are available within _a_ series or available to _all_ series.
 * If the target field is a series or series mark, only fields of that series are considered to be
 * in scope. If the field is located on the secondary axis or is a discrete field of the primary axis
 * (ie. primary/secondary channels per mark channel x|y) then the intersection of fields across all
 * series are considered to be in scope.
 */
export function getCommonFieldsInScopeOfField(encoding: Encoding, fieldId: string): readonly EncField[] {
  /**
   * @description Returns the intersection of sets `a` and `b`
   */
  const intersection = <T>(a: Set<T>, b: Set<T>): Set<T> =>
    new Set(
      [...a].filter((value) => {
        return b.has(value);
      })
    );

  ///
  ///

  // Collect fields by series (*|series)
  const bySeries: ReadonlyArray<EncField>[] = [];
  if (encoding.marks.channel === '*') {
    bySeries.push(collectEncFields(encoding, '*'));
  } else {
    for (const series of encoding.marks.series) {
      bySeries.push(collectEncFields(encoding, series.field));
    }
  }

  // Build the intersection of series fields by formula
  let set: Set<string> | undefined;
  for (const fields of bySeries) {
    // Series includes the field, get the intersection of this set
    // of series formulas with the previous set of series formulas
    if (fields.some(({ id }) => id === fieldId)) {
      const nextSet = new Set(
        fields.map((f) => {
          return f.formula;
        })
      );
      if (set !== undefined) {
        set = intersection(set, nextSet);
      } else {
        set = nextSet;
      }
    }
  }

  // Omit fields that aren't in the common set
  if (set !== undefined) {
    return uniqBy(
      bySeries.flat().filter(({ formula }) => {
        return set?.has(formula);
      }),
      'id'
    );
  }

  // No dice
  return [];
}

export function isSupportedEncodingConversion(
  oldChartType: ChartTypes.ChartType,
  newChartType: ChartTypes.ChartType,
  encoding: Encoding,
  model: SchemaModel,
  format: ChartTypes.ChartFormat
) {
  if (isEncodingEmpty(encoding)) {
    // Allow conversion of empty encoding
    return true;
  }
  // Actually do the conversion to see if it works. If the converted
  // encoding is empty, then it is not supported.
  try {
    const convertedEncoding = convertEncoding(oldChartType, newChartType, encoding, model, format).encoding;
    return !isEncodingEmpty(convertedEncoding);
  } catch {
    return false;
  }
}

export function convertEncoding(
  oldChartType: ChartTypes.ChartType,
  newChartType: ChartTypes.ChartType,
  enc: Encoding,
  model: SchemaModel,
  format?: Readonly<ChartTypes.ChartFormat>
): EncodingResult {
  if (oldChartType === newChartType) {
    // Short circuit when not actually changing the type. https://github.com/mode/muze-issues/issues/785
    return guide.load(enc);
  }
  // When switching from Pivot Table QC or histogram to VE, we can keep the encoding as is.
  if ((oldChartType === 'vegasPivotTable' || oldChartType === 'histogram') && newChartType === 'vegas') {
    return guide.load(enc);
  }
  const unique: Set<ChartTypes.ChartType> = new Set([
    'table',
    'bigValue',
    'vegas',
    'vegasPivotTable',
    'histogram',
  ] as ChartTypes.ChartType[]); // Charts that are incompatible
  // All QC->QC conversions expect the HighCharts version of the encoding.
  const legacyEncoding = getLegacyQuickChartsEncoding(enc, model, oldChartType, format);
  if (StandardXYChartTypes.has(oldChartType) && StandardXYChartTypes.has(newChartType)) {
    const result = convertStandardXYChart(legacyEncoding, oldChartType, newChartType);
    return getRendererSpecificEncodingResult(result, model, newChartType, format);
  } else if (GroupedBarChartTypes.has(oldChartType) && GroupedBarChartTypes.has(newChartType)) {
    const result = convertGroupedBarToGroupedBar(legacyEncoding, oldChartType, newChartType);
    return getRendererSpecificEncodingResult(result, model, newChartType, format);
  } else if (PieChartTypes.has(oldChartType) && PieChartTypes.has(newChartType)) {
    const result = convertPieTypes(legacyEncoding, newChartType);
    return getRendererSpecificEncodingResult(result, model, newChartType, format);
  } else if (
    !PieChartTypes.has(oldChartType) &&
    !PieChartTypes.has(newChartType) &&
    !unique.has(oldChartType) &&
    !unique.has(newChartType)
  ) {
    const result = convertBetweenStandardTypesAndGroupedBar(legacyEncoding, oldChartType, newChartType, model);
    return getRendererSpecificEncodingResult(result, model, newChartType, format);
  } else if (newChartType === 'vegas' && !unique.has(oldChartType) && !isEncodingEmpty(enc)) {
    // When switching to a vegas chart from standard charts, all encodings should be possible
    if (!ChartFormatHelpers.useMuzeRenderer(oldChartType, format)) {
      // If not already rendering the QC in Muze, convert the encoding
      enc = ChartTranslator.translateHighChartsToMuze(oldChartType, enc, format ?? DEFAULT_CHART_FORMAT, model);
    }
    // The translators don't minimize the use of Measure Values to limit the amount
    // of changes necessary. When doing a conversion, we minimize Measure Values
    // to simplify the encoding visible to the user.
    enc = minimizeUseOfMeasureValues(enc);
    return loadEncoding(enc);
  }
  // Should only be here if converting to/from a pie type to a non-pie type
  // or if converting unique chart type
  const blank = initEncoding(newChartType);
  // Copy the filters from the old encoding to the new one.
  return copyEncodingFilters(model, enc, blank.encoding, newChartType);
}

export function copyEncodingFilters(
  model: SchemaModel,
  source: Encoding,
  target: Encoding,
  targetChartType: ChartTypes.ChartType
) {
  const encoder = guide.newBatchOperation(target);
  source.filter.forEach((filter) => {
    const compiled = model.compileEncField(filter);
    if (targetChartType !== 'table' || compiled.isScalar || compiled.isConst) {
      encoder.appendEncFilter(filter);
    }
  });
  return encoder.complete();
}

function convertStandardXYChart(enc: Encoding, oldChartType: ChartTypes.ChartType, newChartType: ChartTypes.ChartType) {
  if (HorizontalChartTypes.has(oldChartType) !== HorizontalChartTypes.has(newChartType)) {
    enc = swapAxes(enc);
  }

  if (
    oldChartType === 'scatter' &&
    enc.marks.series.length > 0 &&
    enc.marks.series[0].properties.size.values.length > 0
  ) {
    enc = guide.removeFields(
      enc,
      enc.marks.series[0].properties.size.values.map((f) => f.id || '') // TODO: check on return value
    ).encoding;
  }

  return guide.updateMarkType(enc, chartToMarkType[newChartType]);
}

function convertGroupedBarToGroupedBar(
  enc: Encoding,
  oldChartType: ChartTypes.ChartType,
  newChartType: ChartTypes.ChartType
) {
  // supposed to be bar to hBar or vice-versa
  if (HorizontalChartTypes.has(oldChartType) !== HorizontalChartTypes.has(newChartType)) {
    return guide.swapRowsColumns(enc);
  }

  // somehow, must have asked to convert to the same chart type?
  // Returning a value to be safe, as this function shouldn't ever return undefined.
  return guide.load(enc);
}

function convertPieTypes(enc: Encoding, newChartType: ChartTypes.ChartType) {
  return guide.updateMarkType(enc, chartToMarkType[newChartType], '*');
}

function convertBetweenStandardTypesAndGroupedBar(
  enc: Encoding,
  oldChartType: ChartTypes.ChartType,
  newChartType: ChartTypes.ChartType,
  model: SchemaModel
) {
  // If not converting to/from pie type, then it should be possible to remap
  const encoder = guide.newBatchOperation(enc);

  // normalize to non-horizontal
  if (HorizontalChartTypes.has(oldChartType)) {
    encoder.swapRowsColumns();
    enc = swapAxes(enc);
  }

  if (oldChartType === 'linePlusBar') {
    if (StandardXYChartTypes.has(newChartType) || GroupedBarChartTypes.has(newChartType)) {
      // move y1 & y2 to value
      if (enc.y.length) {
        encoder.updateField({
          id: enc.y[0].id || '',
          channel: 'value',
        });
      }

      if (enc.y2.length) {
        enc.y2.forEach((y2) => {
          if ((enc.y || []).some((y) => y.formula === y2.formula)) {
            encoder.removeField(y2.id || '');
          } else {
            encoder.updateField({
              id: y2.id || '',
              channel: 'value',
            });
          }
        });
      }

      // Values needs to be on row, and names on color
      encoder.addEncField({ ...VALUES_FIELD }, 'row');
      encoder.addEncFieldToSeries({ ...NAMES_FIELD }, 'color');

      // bar charts need a measure names on X, so add to the end of the column channel
      if (GroupedBarChartTypes.has(newChartType)) {
        convertToDiscrete(model, encoder, enc);
      }

      if (HorizontalChartTypes.has(newChartType)) {
        encoder.swapRowsColumns();
      }

      encoder.updateMarkType(chartToMarkType[newChartType]);
    }
  } else {
    if (GroupedBarChartTypes.has(oldChartType)) {
      // Remove the measure names field
      encoder.removeField(enc.x[0].id || '');
    }

    if (newChartType === 'linePlusBar') {
      // Need to remove color field, if present
      if (enc.marks.series.length && enc.marks.series[0].properties.color.values.length) {
        encoder.removeField(enc.marks.series[0].properties.color.values[0].id || '');
      }

      // Need to remove text field, if present
      if (enc.marks.series.length && enc.marks.series[0].properties.text.values.length) {
        encoder.removeField(enc.marks.series[0].properties.text.values[0].id || '');
      }

      // Need to remove size field, if present
      if (enc.marks.series.length && enc.marks.series[0].properties.size.values.length) {
        encoder.removeField(enc.marks.series[0].properties.size.values[0].id || '');
      }

      // Need to remove values field
      encoder.removeField(enc.y[0].id || '');

      // Take first two value fields and move to y/y2. Delete the rest
      if (enc.value.length > 0) {
        encoder.updateField({
          id: enc.value[0].id || '',
          channel: 'row',
        });
        encoder.updateMarkType(MarkType.Bar, false, enc.value[0].id);
      }

      if (enc.value.length > 1) {
        encoder.updateField({
          id: enc.value[1].id || '',
          channel: 'row',
        });
        encoder.toggleDualAxisField(enc.value[1].id || '');
        encoder.updateMarkType(MarkType.Line, false, enc.value[1].id);
      }

      encoder.removeFields(enc.value.slice(2).map((v) => v.id || ''));
    } else if (GroupedBarChartTypes.has(newChartType)) {
      convertToDiscrete(model, encoder, enc);

      if (
        oldChartType === 'scatter' &&
        enc.marks.series.length > 0 &&
        enc.marks.series[0].properties.size.values.length > 0
      ) {
        encoder.removeFields(enc.marks.series[0].properties.size.values.map((f) => f.id || ''));
      }

      if (HorizontalChartTypes.has(newChartType)) {
        encoder.swapRowsColumns();
      }

      encoder.updateMarkType(MarkType.Bar);
    } else {
      if (HorizontalChartTypes.has(newChartType)) {
        encoder.swapRowsColumns();
      }

      encoder.updateMarkType(chartToMarkType[newChartType]);
    }
  }

  encoder.clearSorts(); // Sorts won't match
  return encoder.complete();
}

function getLegacyQuickChartsEncoding(
  enc: Encoding,
  model: SchemaModel,
  chartType: ChartTypes.ChartType,
  format: ChartTypes.ChartFormat | undefined
) {
  if (ChartFormatHelpers.useMuzeRenderer(chartType, format)) {
    // If we are using the Muze renderer, convert the encoding back to the standard
    // QuickCharts/HighCharts encoding.
    return ChartTranslator.translateMuzeToHighCharts(chartType, enc, format ?? DEFAULT_CHART_FORMAT, model);
  }
  return enc;
}

function getRendererSpecificEncodingResult(
  encodingResult: EncodingResult,
  model: SchemaModel,
  chartType: ChartTypes.ChartType,
  format: ChartTypes.ChartFormat | undefined
) {
  if (ChartFormatHelpers.useMuzeRenderer(chartType, format)) {
    const muzeEncoding = ChartTranslator.translateHighChartsToMuze(
      chartType,
      encodingResult.encoding,
      format ?? DEFAULT_CHART_FORMAT,
      model
    );
    return guide.load(muzeEncoding);
  }
  return encodingResult;
}

function minimizeUseOfMeasureValues(enc: Encoding) {
  // If there is only one field in value, substitute it into all of the places which contain Measure Values.
  if (enc.value.length === 1 && isInEncoding(enc, Values.Key)) {
    const valueField = enc.value[0];
    // First fix any sorts
    if (enc.sorts.length > 0) {
      enc.sorts.forEach((encSort) => {
        if (encSort.type === SortType.FIELD && encSort.byField.formula === Values.Key) {
          // Remove the old sort
          enc = guide.removeSort(enc, encSort.source).encoding;
          // Add a new sort
          const newSort = EncSort.ByField.make(encSort.source, valueField, encSort.descending);
          enc = guide.addSort(enc, newSort).encoding;
        }
      });
    }
    // For Quick Charts conversion, Measure Values is either on x or y if at all.
    const channel = enc.marks.channel === 'x' ? 'column' : 'row';
    const fields = enc.marks.channel === 'x' ? enc.x : enc.y;
    const mvField = fields.find((field) => field.formula === Values.Key);
    if (mvField?.id != null) {
      // Remove the Measure Values field
      enc = guide.removeField(enc, mvField.id).encoding;
      // Remove the value field
      enc = guide.removeField(enc, valueField.id ?? '').encoding;
      // Add in the value field
      enc = guide.addEncField(enc, valueField, channel).encoding;
    }
    // Measure Values may also be on text
    const textMVField = enc.marks.series[0]?.properties.text.values.find((field) => field.formula === Values.Key);
    if (textMVField?.id != null) {
      enc = guide.removeField(enc, textMVField.id).encoding;
      // Create a new EncField and add it to text
      const textField = { ...valueField, id: undefined };
      enc = guide.addEncFieldToSeries(enc, textField, 'text').encoding;
    }
  }
  return enc;
}

export function convertToDiscrete(model: SchemaModel, encoder: BatchEncoder, enc: Encoding) {
  // When switching to a grouped bar chart we have two sets of requirements that need to be checked/performed
  // Requirement 1:
  // -- We need to check if there is a field that is of type continuous
  // -- If there is, we need to update said field to be of type discrete
  // Requirement 2:
  // -- We must add a new $NAMES field to the 'column' channel such that the query will be formatted appropriately

  // Requirement 1
  if (enc.x.length && enc.x[0].variable.type === VariableType.CONTINUOUS) {
    encoder.updateField({
      id: enc.x[0].id || '',
      variable: {
        type: VariableType.DISCRETE,
      },
    });
  }

  // Requirement 2
  // Adding a $NAMES field to the encoding
  // The encoding needs to be otherwise complete in order for the names field to reference the appropriate values
  encoder.addEncField({ ...NAMES_FIELD }, 'column');
}

/**
 * Checks if encoding can be rendered
 * @param enc
 */
export function isChartEncodingComplete(enc: Encoding, chartType: ChartTypes.ChartType): boolean {
  if (!(enc && enc.marks)) {
    return false;
  }

  if (chartType === 'bigValue') {
    if (enc.marks.properties.text.values.length <= 0) {
      return false;
    }
    // TODO: trend line support
  } else if (chartType === 'vegas' || chartType === 'vegasPivotTable') {
    return !isEncodingEmpty(enc);
  } else if (enc.marks.channel === '*' && (chartType === 'pie' || chartType === 'donut' || chartType === 'table')) {
    switch (enc.marks.properties.type.value) {
      case MarkType.Pie:
      case MarkType.Donut:
        if (!enc.marks.properties.angle.values.length || !enc.marks.properties.color.values.length) {
          return false;
        }
        break;
      case MarkType.Text:
        return enc.marks.properties.text.values.length > 0;
      case null: // likely cleared the encoding
        return false;
    }
  } else {
    const { primary, secondary, secondaryDiscrete } = extractPrimarySecondaryAxes(enc);

    if (chartType === 'table' || chartType === 'spreadsheet') {
      return enc.marks.properties.text.values.length > 0;
    }

    if (secondary.length) {
      if (secondary[0].formula === Names.Key) {
        if (!secondaryDiscrete.length) {
          // If measure names is on x-axis, then there must be a dimension on column
          return false;
        }
      }
    } else {
      return false;
    }

    if (primary.length) {
      if (primary[0].formula === Values.Key) {
        if (!enc.value.length) {
          return false; // No point in rendering anything if no measure values
        }
      }
    } else {
      return false;
    }

    if (chartType === 'linePlusBar' && !enc.y2.length) {
      return false;
    }
  }

  return true;
}

export function isRadialEncoding(encoding: Encoding) {
  return (
    encoding.marks.channel === '*' &&
    (encoding.marks.properties.type.value === MarkType.Pie || encoding.marks.properties.type.value === MarkType.Donut)
  );
}

export function swapAxesChartType(chartType: ChartTypes.ChartType): ChartTypes.ChartType {
  switch (chartType) {
    case 'hBar':
      return 'bar';
    case 'bar':
      return 'hBar';
    case 'stackedBar':
      return 'hStackedBar';
    case 'hStackedBar':
      return 'stackedBar';
    case 'stackedBar100':
      return 'hStackedBar100';
    case 'hStackedBar100':
      return 'stackedBar100';
    default:
      return chartType;
  }
}

export function updateField(encoding: Encoding, modifyField: ModifyField): EncodingResult {
  return guide.updateField(encoding, modifyField);
}

export function addNamesFilterToChartEncoding(
  encoding: Encoding,
  model: SchemaModel,
  measureName: string
): EncodingResult {
  const encoder = guide.newBatchOperation(encoding);
  const { row, column, value, x, y, x2, y2 } = encoding;
  const { color, angle, size, tooltip } = encoding.marks.properties;
  const channels =
    encoding.marks.channel === '*'
      ? [row, column, value, x, x2, y, y2]
      : encoding.marks.channel === 'x'
        ? [row, column, value, x, x2]
        : [row, column, value, y, y2];

  channels.forEach((encChannel) => {
    encChannel.forEach((encField) => {
      const field = model.makeField(encField);

      if (!Switch.Fields.isSystem(field as FlamingoTypes.ModeField) && field?.role === Attr.Role.Measure) {
        const fieldName = field.name;

        if (measureName !== fieldName) {
          encoder.removeField(encField.id as string);
        }
      }
    });
  });

  [color, angle, size, tooltip].forEach((encChannel) => {
    encChannel.values.forEach((encField) => {
      const field = model.buildField(encField.formula, encField.variable.type, encField.id);

      if (!Switch.Fields.isSystem(field as FlamingoTypes.ModeField) && field?.role === Attr.Role.Measure) {
        const fieldName = field.name;

        if (measureName !== fieldName) {
          encoder.removeField(encField.id as string);
        }
      }
    });
  });

  return encoder.complete();
}

export function moveMeasureValuesToY(encoding: Encoding): EncodingResult {
  const encoder = guide.newBatchOperation(encoding);

  const { series, properties } = encoding.marks;
  const props = series.length ? series[0].properties : properties;

  const yEncField = encoding.y[0];
  const valueEncField = encoding.value[0];
  const colorEncField = props.color.values[0];

  encoder.removeField(colorEncField.id as string);
  encoder.updateField({ id: valueEncField.id as string, channel: 'row' });
  encoder.removeField(yEncField.id as string);

  return encoder.complete();
}

/**
 * Adds a field to the encoding in the specified channel and mark it dual axis if it is the second
 * field.
 *
 * This is only called for the QuickCharts Bar-Line. It makes assumptions about that configuration
 * and should not be used for Visual Explorer.
 */
export function addFieldAndMakeDual(
  enc: Encoding,
  field: CompiledFormula,
  channel: BasicChannel,
  leftMark: MarkType,
  rightMark: MarkType
) {
  const encoder = guide.newBatchOperation(enc);
  const fields = channel === 'row' ? enc.y : enc.x;
  const dualFields = channel === 'row' ? enc.y2 : enc.x2;

  if (dualFields.length === 1) {
    // Remove the existing dual axis field and replace with the new one.
    // Generally, the UI prevents this except for the case of the typeahead field list
    // and for the Quick Chart Bar-Line
    encoder.removeField(dualFields[0].id ?? '');
  }

  const newField = encoder.addEncField(buildDefaultEncField({ field, zone: ZoneType.Aggregate }), channel);

  if (!newField.id) {
    throw new Error('newField.id is invalid');
  }

  if (fields.length === 0) {
    return encoder.complete();
  }

  encoder.autoEncode();
  encoder.updateMarkType(leftMark, leftMark == null, fields[0].id);
  encoder.updateMarkType(rightMark, rightMark == null, newField.id);
  encoder.toggleDualAxisField(newField.id);

  return ChartTranslatorUtil.ensureMeasureNamesWithDualAxis(encoder, newField.id, channel);
}

/**
 * This function really only works for the following situation:
 * 1. Toggling dual axis on a field that is already in the 'x' or 'y'. In this case,
 *    the move is x->x2 or y->y2.
 * It does not handle moving a field from somewhere else in the encoding into
 * a dual axis.
 */
export function moveFieldAndMakeDual(
  enc: Encoding,
  field: CompiledField,
  channel: BasicChannel,
  leftMark: MarkType,
  rightMark: MarkType
) {
  const encoder = guide.newBatchOperation(enc);
  const fields = channel === 'row' ? enc.y : enc.x;

  if (!field.isBinned) {
    encoder.updateField({
      ...buildUpdateFieldParams(field, ZoneType.Aggregate),
    });
  }

  if (!field.alias) {
    throw new Error('field.alias is invalid');
  }

  if (fields.length === 0) {
    return encoder.complete();
  }
  const rightIndex = fields.findIndex((f) => f.id === field.alias);
  const leftIndex = Math.max(0, rightIndex);

  encoder.autoEncode();
  encoder.updateMarkType(leftMark, leftMark == null, fields[leftIndex].id);
  encoder.updateMarkType(rightMark, rightMark == null, field.alias);
  encoder.toggleDualAxisField(field.alias);

  return ChartTranslatorUtil.ensureMeasureNamesWithDualAxis(encoder, field.alias, channel);
}

/**
 * This function handles the case that moveFieldAndMakeDual does not.
 * Specifically, moving a field to a dual axis from elsewhere in the encoding.
 * This can only be done in the Quick Charts Bar-Line.
 */
export function moveFieldToDualAxis(
  enc: Encoding,
  field: CompiledField,
  channel: BasicChannel,
  leftMark: MarkType,
  rightMark: MarkType,
  dropIndex: number | undefined
) {
  if (!field.alias) {
    throw new Error('field.alias is invalid');
  }

  const encoder = guide.newBatchOperation(enc);
  const fields = channel === 'row' ? enc.y : enc.x;

  const dualFields = channel === 'row' ? enc.y2 : enc.x2;

  const fieldIsPrimary = fields.some((f) => f.id === field.alias);
  const fieldIsDualAxis = dualFields.some((f) => f.id === field.alias);

  if (fieldIsPrimary || fieldIsDualAxis) {
    // Moving one of the dual axis fields around. Need to handle this differently
    // from a field from somewhere else in the encoding.

    // Case 1: Moving the primary field when there is no secondary
    // Should be a no-op
    if (fieldIsPrimary && dualFields.length === 0) {
      return;
    }

    // Case 2: Moving the primary field above/below itself
    // Should be a no-op
    if (fieldIsPrimary && (dropIndex ?? 0) < 2) {
      return;
    }

    // Case 3: Moving the dual axis field above/below itself
    // Should be a no-op
    if (fieldIsDualAxis && (dropIndex ?? 0) > 0) {
      return;
    }

    // Case 4: Anything else is a swap
    const primary = fields[0];
    const dual = dualFields[0];
    if (primary?.id && dual?.id) {
      // Remove both fields
      encoder.removeField(dual.id);
      encoder.removeField(primary.id);

      // Add old dual field to primary axis
      encoder.addEncField(dual, channel);

      // Add old primary field to primary axis as the second/right field
      encoder.addEncField(primary, channel);

      // Toggle dual axis on the old primary field (i.e. new dual field)
      encoder.autoEncode();
      encoder.updateMarkType(leftMark, leftMark == null, dual.id);
      encoder.updateMarkType(rightMark, rightMark == null, primary.id);
      encoder.toggleDualAxisField(primary.id);

      return encoder.complete();
    }
  }

  if (dualFields.length === 1) {
    // If there is already a dual field, remove and and replace with new field
    encoder.removeField(dualFields[0].id ?? '');
  }

  encoder.removeField(field.alias);
  const newField = encoder.addEncField(buildDefaultEncField({ field, zone: ZoneType.Aggregate }), channel);

  if (!newField.id) {
    throw new Error('newField.id is invalid');
  }

  if (fields.length === 0) {
    return encoder.complete();
  }

  encoder.autoEncode();
  encoder.updateMarkType(leftMark, leftMark == null, fields[0].id);
  encoder.updateMarkType(rightMark, rightMark == null, newField.id);
  encoder.toggleDualAxisField(newField.id);

  return ChartTranslatorUtil.ensureMeasureNamesWithDualAxis(encoder, field.alias, channel);
}

export function buildUpdateFieldParams(field: CompiledField, zone: ZoneType): ModifyField {
  if (!field.alias) {
    throw new Error('field.alias is invalid');
  }

  const opts: ModifyField = {
    id: field.alias,
  };

  switch (field.dataType) {
    case DataType.Name.String:
    case DataType.Name.Boolean:
      switch (zone) {
        case ZoneType.Aggregate:
          if (!field.hasAggFunc) {
            opts.formula = `COUNT([${field.attrName}])`;
            opts.variable = {
              type: VariableType.CONTINUOUS,
            };
          }
          break;
        case ZoneType.Continuous:
        case ZoneType.Discrete:
          opts.formula = `[${field.attrName}]`;
          opts.variable = {
            type: VariableType.DISCRETE,
          };
          break;
      }
      break;
    case DataType.Name.DateTime:
    case DataType.Name.Timestamp:
      switch (zone) {
        case ZoneType.Discrete:
          if (!field.hasDatePart) {
            opts.formula = `DATEPART('${DateTime.Unit.Year}', [${field.attrName}])`;
            opts.variable = {
              type: VariableType.DISCRETE,
            };
          }
          break;
        case ZoneType.Continuous:
          if (!field.hasDateTrunc) {
            opts.formula = `DATETRUNC('${DateTime.Unit.Day}', [${field.attrName}])`;
            opts.variable = {
              type: VariableType.CONTINUOUS,
            };
          }
          break;
        case ZoneType.Aggregate:
          if (!field.hasAggFunc) {
            opts.formula = `COUNT([${field.attrName}])`;
            opts.variable = {
              type: VariableType.CONTINUOUS,
            };
          }
          break;
      }
      break;
    case DataType.Name.Integer:
    case DataType.Name.Float:
    case DataType.Name.Decimal:
      switch (zone) {
        case ZoneType.Discrete:
          opts.formula = `[${field.attrName}]`;
          opts.variable = {
            type: VariableType.DISCRETE,
          };
          break;
        case ZoneType.Continuous:
          opts.formula = `[${field.attrName}]`;
          opts.variable = {
            type: VariableType.CONTINUOUS,
          };
          break;
        case ZoneType.Aggregate:
          if (field.hasAggFunc) {
            opts.formula = `${field.aggFunc}([${field.attrName}])`;
          } else {
            opts.formula = field.isAggregateable ? `SUM([${field.attrName}])` : `[${field.attrName}]`;
          }

          opts.variable = {
            type: VariableType.CONTINUOUS,
          };
          break;
      }
      if (field.isValues) {
        opts.formula = field.formula;
      }
      break;
    default:
      return {
        id: field.alias,
      };
  }

  return opts;
}

export function makeFieldDomainQueryEncoding(
  encoding: Encoding,
  field: CompiledFormula,
  withFilters: 'all' | 'none' | 'otherFields'
) {
  if (withFilters === 'none') {
    // Remove all the filters
    const encoder = guide.newBatchOperation(encoding);
    encoder.clearFilters();
    return encoder.complete().encoding;
  } else if (withFilters === 'otherFields') {
    // Remove filter associated with the filter field
    const encoder = guide.newBatchOperation(encoding);
    encoder.removeFilter(field.formula, field.isDiscrete);
    return encoder.complete().encoding;
  }
  // Just return the current encoding
  const encoder = guide.newBatchOperation(encoding);
  return encoder.complete().encoding;
}

/**
 * Create a simplified version of an Encoding that doesn't include any user identifying
 * data like field formulas, data values, etc. and keeps the size of the summary as small
 * as possible. This representation can be used for capturing debug information for tools
 * such as BugSnag.
 *
 * The returned object does not include any field formulas as those can identify user
 * information. Each field is represented by an id. Unlike the EncField id though, this
 * id may be used in multiple places if the same formula is present in multiple places
 * in the encoding. For example, if 'SUM[price]' appears in both the 'y' and 'color', it
 * is included in both places using the same id.
 *
 * @param enc Encoding to convert
 * @returns Object representing the encoding
 */
export function summarizeEncoding(enc: Encoding) {
  const summary: any = {};
  // Reuse the same id for fields with the same formula. This map allows for lookup of
  // an existing id for a given formula.
  const formulaToIdMap: Record<string, string> = {};
  // Map of the original id to the original formula. This is necessary for series id
  // which references the field by id in the x or y.
  const originalIdToFormulaMap: Record<string, string> = {};

  const getIdToRepresentFormula = (formula: string, id: string | undefined) => {
    if (formulaToIdMap[formula] == null) {
      formulaToIdMap[formula] = formula === Names.Key || formula === Values.Key ? formula : (id ?? 'ENC0');
    }
    if (id != null) {
      originalIdToFormulaMap[id] = formula;
    }
    return formulaToIdMap[formula];
  };

  const summarizeField = (field: Readonly<EncField> | null | undefined) => {
    return field != null
      ? {
          id: getIdToRepresentFormula(field.formula, field.id),
          frame: field.frame,
          type: field.variable.type,
          isFixed: field.variable.fixed,
          level: field.variable.level,
        }
      : undefined;
  };
  const summarizeEncFieldArray = (role: string, fields: ReadonlyArray<Readonly<EncField>>, parent: any) => {
    if (fields.length === 0) {
      return;
    }
    parent[role] = fields.map((field) => {
      return summarizeField(field);
    });
  };

  // Role Arrays
  summarizeEncFieldArray('x', enc.x, summary);
  summarizeEncFieldArray('x2', enc.x2, summary);
  summarizeEncFieldArray('y', enc.y, summary);
  summarizeEncFieldArray('y2', enc.y2, summary);
  summarizeEncFieldArray('row', enc.row, summary);
  summarizeEncFieldArray('column', enc.column, summary);
  summarizeEncFieldArray('value', enc.value, summary);

  const summarizeProperties = (properties: Readonly<EncMarks>) => {
    const markObj: any = {};
    Object.entries(properties).forEach(([key, value]) => {
      if (Object.prototype.hasOwnProperty.call(value, 'value')) {
        markObj[key] = (value as any).value;
      }
      if (Object.prototype.hasOwnProperty.call(value, 'values') && (value as any).values.length > 0) {
        summarizeEncFieldArray(`${key}Fields`, (value as any).values, markObj);
      }
    });
    return markObj;
  };
  // Marks
  summary['marks'] = {
    channel: enc.marks.channel,
    props: enc.marks.channel === '*' ? summarizeProperties(enc.marks.properties) : undefined,
    series: enc.marks.series.map((series) => {
      return {
        field: getIdToRepresentFormula(originalIdToFormulaMap[series.field], series.field),
        props: summarizeProperties(series.properties),
      };
    }),
  };

  // Filters
  if (enc.filter.length > 0) {
    summary['filters'] = enc.filter.map((filter) => {
      const summaryFilter: any = {
        type: filter.filterType,
        id: getIdToRepresentFormula(filter.formula, filter.id),
        variableType: filter.variable.type,
      };
      switch (filter.filterType) {
        case FilterType.Discrete:
        case FilterType.DiscreteBoolean:
          summaryFilter['mode'] = filter.selection.mode;
          summaryFilter['numSelected'] = filter.selection.selected.length;
          break;
        case FilterType.Continuous:
        case FilterType.ContinuousDate:
          summaryFilter['min'] = filter.range.min;
          summaryFilter['max'] = filter.range.max;
          summaryFilter['includeMax'] = filter.includeMax;
          summaryFilter['includeMin'] = filter.includeMin;
          summaryFilter['includeNulls'] = filter.includeNulls;
          break;
        case FilterType.RelativeDate:
          summaryFilter['anchor'] = filter.anchor;
          summaryFilter['weekStart'] = filter.weekStart;
          summaryFilter['yearStart'] = filter.yearStart;
          summaryFilter['includeNulls'] = filter.includeNulls;
          // @todo(kg): from/to
          break;
        case FilterType.TopByField:
          summaryFilter['byFieldId'] = getIdToRepresentFormula(filter.cond.byField.formula, filter.cond.byField.id);
          summaryFilter['byFieldVariableType'] = filter.cond.byField.variable.type;
          summaryFilter['operator'] = filter.cond.operator;
          summaryFilter['sequence'] = filter.cond.sequence;
          summaryFilter['limit'] = filter.cond.limit;
          break;
        default:
          break;
      }
      return summaryFilter;
    });
  }
  // Sorts
  if (enc.sorts.length > 0) {
    summary['sorts'] = enc.sorts.map((sort) => {
      const byField =
        sort.type === SortType.FIELD || sort.type === SortType.NESTED ? summarizeField(sort.byField) : void 0;
      return {
        type: sort.type,
        source: sort.source,
        descending: sort.descending,
        byField,
      };
    });
  }
  // Totals
  if (enc.totals?.grand.row || enc.totals?.grand.column) {
    summary['totals'] = enc.totals;
  }

  return summary;
}

export function getMarkTypes(encoding: Encoding | undefined) {
  if (!encoding) {
    return [];
  }
  if (encoding.marks.channel === '*') {
    return [encoding.marks.properties.type.value ?? ''];
  }
  return encoding.marks.series.map((series) => series.properties.type.value ?? '');
}

export function isDateType(filter: ReportTypes.FilterDefinition): boolean {
  switch (filter.formulaType) {
    case ReportDataType.DATE:
    case ReportDataType.DATETIME:
      return true;
    default:
      return false;
  }
}

export function convertSelectedValuesToDates(value: ReportTypes.ReportFilterRange) {
  const { min, max } = value;

  const minDate = min == null ? null : new Date(min);
  const maxDate = max == null ? null : new Date(max);

  if (minDate != null && isValid(minDate)) {
    minDate.setUTCHours(0, 0, 0, 0); // set to start of day
  }

  if (maxDate != null && isValid(maxDate)) {
    maxDate.setUTCHours(23, 59, 59, 999); // set last second of day
  }

  /**
   * Force casting here because vegas EncFilterField has a mistake on min/max
   * where it doesn't accept null values.
   **/

  return {
    min: (minDate && minDate.valueOf()) as number,
    max: (maxDate && maxDate.valueOf()) as number,
  };
}

/**
 * This function was primarily present in ReportFilterHelpers but added it
 * here as well, to import it into chart-plan.effects.ts avoiding circular dependency between
 * report-viewer/api and charts/api
 */
export function addReportFiltersToChartEncoding(
  view: FlamingoTypes.ModeView | undefined,
  encoding: Encoding,
  filters: ReadonlyArray<ReportTypes.ReportFilterControl>,
  merge = false
): Encoding {
  if (filters == null || filters.length <= 0) {
    return encoding;
  }

  const encFilters = filters
    .map<EncFilterField | undefined>((filter) => {
      switch (filter.filterType) {
        case ReportTypes.ReportFilterType.Range:
          if (isDateType(filter)) {
            const selectedAsDates = convertSelectedValuesToDates(filter.selected);
            return {
              formula: filter.formula,
              variable: { type: VariableType.CONTINUOUS },
              filterType: FilterType.ContinuousDate,
              range: { max: selectedAsDates.max, min: selectedAsDates.min },
              includeMin: true,
              includeMax: false,
              includeNulls: false, // Not used in the UI currently
            };
          } else {
            return {
              formula: filter.formula,
              variable: { type: VariableType.CONTINUOUS },
              filterType: FilterType.Continuous,
              range: { max: filter.selected.max as number, min: filter.selected.min as number },
              includeMin: true,
              includeMax: true,
              includeNulls: false, // Not used in the UI currently
            };
          }
        case ReportTypes.ReportFilterType.Multi:
          if (!filter.selected.all) {
            return {
              formula: filter.formula,
              variable: { type: VariableType.DISCRETE },
              filterType: FilterType.Discrete,
              selection: {
                selected: filter.selected.values ? Array.from(filter.selected.values) : [],
                mode: SelectionMode.INCLUDE,
              },
            };
          }
          break;
        case ReportTypes.ReportFilterType.Single:
          if (!filter.selected.all) {
            if (filter.dataType === ReportDataType.BOOLEAN) {
              return {
                formula: filter.formula,
                variable: { type: VariableType.DISCRETE },
                filterType: FilterType.DiscreteBoolean,
                selection: {
                  selected: [filter.selected.value as ReportTypes.ReportFilterValue],
                  mode: SelectionMode.INCLUDE,
                },
              };
            } else {
              return {
                formula: filter.formula,
                variable: { type: VariableType.DISCRETE },
                filterType: FilterType.Discrete,
                selection: {
                  selected: [filter.selected.value as ReportTypes.ReportFilterValue],
                  mode: SelectionMode.INCLUDE,
                },
              };
            }
          }
          break;
      }
    })
    .filter(isPresent);

  const encoder = guide.newBatchOperation(encoding);

  if (view != null && merge) {
    for (const filter of encoding.filter) {
      /**
       * Ensure relative date filters have the correct w/y start values before attempting
       * to merge in report filters. When combining date filters, Vegas converts relative
       * date filters to continuous date filters such that the resulting filter is a simple
       * intersection of the two ranges; we provide the workspace w/y config for relative
       * date filters that don't have one or both defined
       */
      if (filter.filterType === FilterType.RelativeDate) {
        const yearStart = DateTime.yearStartMonth(filter.yearStart ?? view.yearStart);
        const weekStart = DateTime.weekStartDow(filter.weekStart ?? view.weekStart);
        // "addEncFilter" is an upsert
        encoder.addEncFilter({
          ...filter,
          yearStart,
          weekStart,
        });
      }
    }
  }

  encFilters.forEach((f) => {
    if (merge) {
      encoder.mergeEncFilter(f);
    } else {
      encoder.appendEncFilter(f);
    }
  });

  return encoder.complete().encoding;
}

export function generateHistogramQCEncoding(
  encoding: Encoding,
  field: CompiledFormula,
  binSource: string,
  at?: string,
  zoneType?: ZoneType
) {
  const encoder = guide.newBatchOperation(encoding);
  encoder.addEncField(buildDefaultEncField({ field, zone: zoneType }), 'row', at);
  encoder.addEncField(buildDefaultEncField({ field, zone: zoneType }), 'column', at);

  let result = encoder.complete();

  result = updateFieldEncoding(result.encoding, {
    id: result.encoding.x[0].id || '',
    formula: binSource,
    variable: {
      type: field.varType === 'CONTINUOUS' ? VariableType.CONTINUOUS : VariableType.DISCRETE,
    },
  });

  result = updateFieldEncoding(result.encoding, {
    id: result.encoding.y[0].id || '',
    formula: FormulaOperations.aggregate(field, ExprCall.Name.Count),
    variable: {
      type: field.varType === 'CONTINUOUS' ? VariableType.CONTINUOUS : VariableType.DISCRETE,
    },
  });

  result = updateFieldMarkType(result.encoding, undefined, MarkType.Bar);

  return result;
}

export function addBasicFieldToEncoding(
  encoding: Encoding,
  field: CompiledFormula,
  channel: BasicChannel,
  at?: string,
  zoneType?: ZoneType
) {
  const encoder = guide.newBatchOperation(encoding);
  encoder.addEncField(buildDefaultEncField({ field, zone: zoneType }), channel, at);
  return encoder.complete();
}
